import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import AnchoredPopover from './AnchoredPopover'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Send,
  RotateCw,
  X,
  Search,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Bot,
  Maximize2,
  Info,
  ExternalLink,
  Clock,
  Upload,
  XCircle,
  SendHorizonal,
  FileText,
  FileSpreadsheet,
  ClipboardList,
  Activity,
  Sparkles,
  History,
  Layers,
  RefreshCw,
  Check,
} from 'lucide-react'
import PDFViewer from './PDFViewer'
import CostBreakdown from './CostBreakdown'
import RequestPanelTabs from './requestDetail/RequestPanelTabs'
import FieldsPanelHeader from './requestDetail/FieldsPanelHeader'
import ActivityLogPanel from './requestDetail/ActivityLogPanel'
import EditGate from './requestDetail/EditGate'
import { nsApi, requestsApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useDisplayPreferences } from '../context/DisplayPreferencesContext'
import { isRequestLocked } from '../utils/requestStatus'
import { isReadOnlyUser } from '../utils/readerAccess'
import { formatAuditLogsForActivity } from '../utils/auditLog'
import { formatDateTime, formatTimeAgo } from '../utils/dateUtils'
import useSegmentedTabIndicator from '../hooks/useSegmentedTabIndicator'
import {
  NS_BASELINE_BUNDLE,
  compileBundle,
  validateNsPayload,
} from '../utils/nsValidation'
import OpCoBadge from './opco/OpCoBadge'
import { MoveOpCoButton, UnassignedOpCoBanner } from './opco/MoveOpCoDialog'

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getInputType(field) {
  const ft = (field.field_type || '').toLowerCase()
  const ns = (field.ns_field || '').toLowerCase()
  const dn = (field.display_name || '').toLowerCase()
  if (ft === 'date' || ns.includes('date') || dn.includes('date')) return 'date'
  if (
    ft === 'number' || ft === 'integer' || ft === 'currency' || ft === 'decimal' ||
    ns.includes('amount') || ns.includes('quantity') || ns.includes('price') ||
    ns.includes('qty') || ns.includes('total') || ns.includes('tax')
  ) return 'number'
  return 'text'
}

export function normaliseForInput(value, inputType) {
  if (value === null || value === undefined || value === '') return ''
  if (inputType === 'date') {
    const s = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    try {
      const d = new Date(s)
      if (!isNaN(d)) {
        // Use LOCAL date parts. toISOString() converts to UTC, which rolls the day
        // back by one in zones ahead of UTC (e.g. AEST +10: 24-JUN → 23-JUN).
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }
    } catch { /* best effort — fall through */ }
    return s
  }
  if (inputType === 'number') {
    // Extracted numbers often carry thousands separators / currency symbols
    // (e.g. "1,847.16", "$581.29") which <input type="number"> silently refuses
    // to render — the value is set but the box shows blank. Strip everything
    // that isn't a digit, sign, or decimal point before handing it to the input.
    const s = String(value).trim()
    const cleaned = s.replace(/[^0-9.-]/g, '')
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return ''
    const n = parseFloat(cleaned)
    return isNaN(n) ? '' : String(n)
  }
  return String(value)
}

// Corporate-entity suffixes common enough that matching on them alone is
// meaningless — nearly every Australian company name ends in "Pty Ltd", so
// without excluding these, searching "Harvey Norman Pty Ltd" scored every
// other "... Pty Ltd" company (2 of 4 words matching) above zero and buried
// the real match in a wall of unrelated results that merely shared the
// suffix. Distinguishing a company means matching its actual name, not its
// legal form.
const CORPORATE_SUFFIX_STOPWORDS = new Set([
  'pty', 'ltd', 'limited', 'inc', 'incorporated', 'llc', 'llp', 'co', 'corp',
  'corporation', 'plc', 'group', 'holdings', 'the',
])

function fuzzyScore(query, target) {
  if (!query || !target) return 0
  const q = query.toLowerCase().trim()
  const t = target.toLowerCase().trim()
  if (t === q) return 1
  if (t.startsWith(q)) return 0.9
  if (t.includes(q)) return 0.7
  const qw = q.split(/\s+/).filter(w => w && !CORPORATE_SUFFIX_STOPWORDS.has(w))
  const tw = t.split(/\s+/).filter(Boolean)
  if (qw.length === 0) return 0
  const matched = qw.filter(w => tw.some(tw2 => tw2.startsWith(w)))
  return 0.4 * (matched.length / qw.length)
}

// ─── Match confidence — how close is what the backend resolved to what the ───
// document actually said, as a real percentage (Levenshtein-based), not the
// coarse word-overlap fuzzyScore above. The backend's "matched" flag only
// means "resolved to some NS record" — it does not mean the names line up.
// Without this, a reseller extracted as "Kimpton Margot Sydney" auto-picking
// NS record "Hire Intelligence - Central Sydney MWAU" (a real case: the
// picked record was linked by ID, not name) rendered identically to a true
// exact match — solid green, no way to tell them apart at a glance.
function levenshtein(a, b) {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      cur[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], cur[j - 1])
    }
    prev = cur
  }
  return prev[n]
}

// 0..1 — 1 means identical (case/whitespace-insensitive).
function matchSimilarity(extracted, resolvedName) {
  const a = (extracted || '').toLowerCase().trim()
  const b = (resolvedName || '').toLowerCase().trim()
  if (!a || !b) return 0
  if (a === b) return 1
  const dist = levenshtein(a, b)
  return Math.max(0, 1 - dist / Math.max(a.length, b.length))
}

// Thresholds: >=95% exact-enough to trust silently, 70-95% close but worth a
// glance, below that the resolved record likely isn't what the document said.
function matchLevel(similarity) {
  if (similarity >= 0.95) return 'high'
  if (similarity >= 0.7) return 'medium'
  return 'low'
}

const MATCH_LEVEL_CLASSES = {
  high: { border: 'border-green-400 dark:border-green-600', text: 'text-green-600 dark:text-green-400' },
  medium: { border: 'border-yellow-400 dark:border-yellow-600', text: 'text-yellow-600 dark:text-yellow-400' },
  low: { border: 'border-red-400 dark:border-red-600', text: 'text-red-600 dark:text-red-400' },
}

// ─── Hover tooltip — explains why a field is flagged, shown on hover only ─────

export function FieldTooltip({ text, children }) {
  if (!text) return children
  return (
    <div className="relative group/tt w-full">
      {children}
      <div className="pointer-events-none absolute z-50 bottom-full left-0 mb-1.5 opacity-0 translate-y-1 invisible group-hover/tt:opacity-100 group-hover/tt:translate-y-0 group-hover/tt:visible transition-all duration-150">
        <div className="rounded-md bg-gray-900 dark:bg-gray-950 text-white text-[11px] leading-snug px-2.5 py-1.5 shadow-xl max-w-xs whitespace-normal">
          {text}
          <div className="absolute top-full left-3 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
        </div>
      </div>
    </div>
  )
}

// ─── Generic searchable dropdown (used for subsidiary, location, currency) ────

export function SearchableDropdown({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  loading = false,
  required = false,
  filterFn,
  extractedText = '',
  // Fires with the raw typed text whenever the search box changes — for a
  // caller that needs to react to what the user is typing (e.g. running its
  // own live/cached search). NOT `filterFn`: that gets passed straight into
  // Array.prototype.filter, which calls it as (element, index, array) — the
  // second argument there is the array index, not the query string.
  onQueryChange,
  // True while a caller-owned live search (the one onQueryChange triggers) is
  // in flight, so the box can show a spinner instead of the static chevron —
  // matching VendorField/ItemSearchDropdown, which both do this already.
  searching = false,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const dropRef = useRef(null)
  const prefilledExtracted = useRef(false)

  const selected = useMemo(() => options.find(o => String(o.id) === String(value) || o.name === value), [options, value])

  // If the AI extracted a raw value for this field but nothing auto-matched it
  // to an NS record, seed the search box with that text so the user sees what
  // was found on the document and can pick the right record in one click.
  useEffect(() => {
    if (!prefilledExtracted.current && !selected && !query && extractedText) {
      prefilledExtracted.current = true
      setQuery(extractedText)
    }
  }, [selected, query, extractedText])

  const filtered = useMemo(() => {
    let src = filterFn ? options.filter(filterFn) : options
    if (!query.trim()) return src.slice(0, 80)
    return src
      .map(o => ({ ...o, _s: Math.max(fuzzyScore(query, o.name || ''), fuzzyScore(query, String(o.id) || '')) }))
      .filter(o => o._s > 0)
      .sort((a, b) => b._s - a._s)
      .slice(0, 80)
  }, [options, query, filterFn])

  useEffect(() => { onQueryChange?.(query) }, [query, onQueryChange])

  useEffect(() => {
    function h(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const isValid = !!selected
  const isMissing = !loading && required && !value
  // Extracted a value from the document but it didn't match any NS record —
  // flag it even when the field isn't required, so nothing silently drops.
  const isUnmatchedExtraction = !loading && !isValid && !!extractedText
  const needsAttention = isMissing || isUnmatchedExtraction
  const borderClass = isValid
    ? 'border-green-400 dark:border-green-600'
    : needsAttention
    ? 'border-2 border-amber-400 dark:border-amber-500 hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-700'
    : 'border-gray-300 dark:border-gray-700'
  const fieldLabel = placeholder.replace('Select ', '').replace('…', '')
  const tooltipText = isMissing
    ? `${fieldLabel} is required — NetSuite will reject this PO until it's set.`
    : isUnmatchedExtraction
    ? `Extracted "${extractedText}" but it didn't match an existing NetSuite ${fieldLabel} — search and select the correct one.`
    : null

  return (
    <FieldTooltip text={tooltipText}>
    <div ref={dropRef} className="relative">
      {/* Always an editable input — never a button that only shows the static
          placeholder while closed. That was the bug: the extracted text was
          sitting correctly in `query` (prefilled above) but a closed button
          only ever rendered `selected.name` or `placeholder`, so the box
          looked empty even though the app had it. Matches VendorField /
          ItemSearchDropdown, which are built the same way for the same
          reason: what the document said stays visible in the box, and the
          confirmed NS record — once picked — is the line below, not a
          silent swap of the box's own text. */}
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 transition-colors duration-200 ${borderClass} ${loading ? 'opacity-60' : ''}`}>
        <Search className="h-3.5 w-3.5 text-gray-400 ml-2.5 flex-shrink-0" />
        <input
          type="text"
          value={loading ? '' : query}
          disabled={loading}
          placeholder={loading ? 'Loading…' : placeholder}
          onFocus={() => !loading && setOpen(true)}
          onChange={e => {
            setQuery(e.target.value)
            setOpen(true)
            if (selected) onChange(null) // editing away from a confirmed match clears it, same as VendorField
          }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-2 py-2 bg-transparent focus:outline-none min-w-0 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 disabled:cursor-wait"
        />
        {loading || searching
          ? <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin mr-2.5 flex-shrink-0" />
          : (
            <button type="button" onClick={() => setOpen(o => !o)} className="p-1.5 mr-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0">
              <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          )
        }
      </div>

      {isValid && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
          <span className="font-mono font-semibold">{selected.id}</span>
          {selected.name !== String(selected.id) && <><span className="text-gray-400">—</span><span>{selected.name}</span></>}
        </p>
      )}
      {!isValid && !searching && isUnmatchedExtraction && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Extracted "{extractedText}" — not matched, please select
        </p>
      )}

      {open && !loading && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-64 overflow-hidden flex flex-col animate-panel-in">
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && <p className="px-3 py-3 text-xs text-gray-400 italic text-center">No results</p>}
            {filtered.map(o => (
              <button
                key={o.id}
                onClick={() => { onChange(o); setOpen(false); setQuery(o.name || '') }}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between group"
              >
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-700 truncate">{o.name}</span>
                <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{o.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    </FieldTooltip>
  )
}

// ─── VendorField (NS variant) — resolves to integer internal ID ───────────────

export function VendorField({ extractedValue, vendors, onVendorResolved, onVendorCleared, requestId, orgId, subsidiaryId, templateId, defaultVendor = null }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(extractedValue || '')
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(false)
  const [resolved, setResolved] = useState(null) // { id: 123, name: 'Vendor Name' }
  const [serverResults, setServerResults] = useState(null)
  const dropRef = useRef(null)
  const hasFetched = useRef(false)
  const lastSearchRef = useRef('')

  // Live NetSuite vendor search (debounced 350ms, same as the item dropdown),
  // querying the FULL vendor table server-side. `vendors` (lookups.vendors) is
  // only ever the pre-search fallback. Only runs while the dropdown is open, so
  // it doesn't fire on mount.
  //
  // subsidiaryId is passed only when the subsidiary was chosen independently of
  // the vendor — see the caller. Scoping by a subsidiary that was itself derived
  // from the resolved vendor would be circular and could hide the very vendor
  // being searched for.
  useEffect(() => {
    const q = query.trim()
    if (!open || q.length < 2) { setServerResults(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      nsApi.searchVendors(q, subsidiaryId)
        .then(res => { if (!cancelled) setServerResults(res.data?.vendors || []) })
        .catch(() => { if (!cancelled) setServerResults([]) })
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, open, subsidiaryId])

  const sortedGroups = useMemo(() => {
    // Priority: resolveVendor's fuzzy candidates > live server search results (full
    // catalog) > the capped bulk list (only ever a pre-search fallback).
    const source = candidates.length > 0
      ? candidates
      : (serverResults && serverResults.length > 0)
        ? serverResults
        : vendors.map(v => ({ id: v.id || v.internalId, name: v.name, subsidiary: v.subsidiary }))
    const scored = source.map(c => ({
      ...c,
      _score: Math.max(fuzzyScore(query, c.name || ''), fuzzyScore(query, String(c.id) || '')),
    }))
    const top = scored.filter(c => c._score > 0).sort((a, b) => b._score - a._score)
    const bottom = scored.filter(c => c._score === 0)
    return { top, bottom }
  }, [candidates, serverResults, vendors, query])

  // A configured default (e.g. a Vendor Bid template's Supplier) stands in
  // when the document names no vendor. Never overrides an extracted value.
  useEffect(() => {
    if (extractedValue || resolved || !defaultVendor?.id) return
    setResolved({ id: defaultVendor.id, name: defaultVendor.name || '' })
    setQuery(defaultVendor.name || '')
    onVendorResolved(defaultVendor.id, defaultVendor.name || '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultVendor?.id, extractedValue])

  useEffect(() => {
    if (!extractedValue || hasFetched.current) return
    hasFetched.current = true
    setLoading(true)
    nsApi.resolveVendor({ request_id: requestId, vendor_name: extractedValue, org_id: orgId })
      .then(res => {
        const d = res.data || {}
        if (d.matched || d.resolved) {
          // Keep showing exactly what the document says (extractedValue, already in
          // `query`) — the resolved NS legal name only appears in the confirmation
          // line below. Overwriting the box here would hide a bad auto-match (e.g. a
          // fuzzy hit on the wrong entity) behind a name the user never typed or saw.
          setResolved({ id: d.ns_vendor_id, name: d.ns_vendor_name })
          onVendorResolved(d.ns_vendor_id, d.ns_vendor_name, d.ns_vendor_subsidiary)
        }
        // Anything short of the backend's own near-exact match is left
        // unmatched on purpose — no auto-suggested candidate list (same rule
        // as CustomerField). A fuzzy "closest guess" sitting under the field
        // reads as a real answer; a reviewer types their own search below
        // (doSearch) to find and confirm the real record instead.
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function doSearch(q) {
    const trimmed = q.trim()
    if (trimmed.length < 2 || trimmed === lastSearchRef.current) return
    lastSearchRef.current = trimmed
    setLoading(true)
    try {
      const res = await nsApi.resolveVendor({ vendor_name: trimmed, org_id: orgId })
      const d = res.data || {}
      if (d.matched || d.resolved) {
        // Same rule as the auto-resolve effect: leave the typed/extracted text as-is,
        // don't silently swap it for the matched NS name.
        setResolved({ id: d.ns_vendor_id, name: d.ns_vendor_name })
        onVendorResolved(d.ns_vendor_id, d.ns_vendor_name, d.ns_vendor_subsidiary)
        setOpen(false)
      } else {
        setCandidates(d.candidates || [])
        setOpen(true)
      }
    } catch { /* best effort — fall through */ } finally { setLoading(false) }
  }

  function handleSelect(candidate) {
    setResolved({ id: candidate.id, name: candidate.name })
    // Same reason as ItemSearchDropdown's option click: query is read with
    // .trim() on the next render, so it must never become undefined.
    setQuery(candidate.name || '')
    setCandidates([])
    setOpen(false)
    onVendorResolved(candidate.id, candidate.name, candidate.subsidiary)
    // template_id lets the backend also re-detect this vendor's field usage into
    // the template's rules — a newly-onboarded vendor is exactly when a
    // subsidiary-specific mandatory field tends to appear.
    nsApi.confirmVendor({
      org_id: orgId, pdf_name: extractedValue || candidate.name,
      ns_vendor_id: candidate.id, ns_vendor_name: candidate.name, user_id: '',
      template_id: templateId || undefined,
    }).catch(() => {})
  }

  function handleClear() {
    setResolved(null); setQuery(extractedValue || ''); setCandidates([]); setServerResults(null); setOpen(false)
    hasFetched.current = false; lastSearchRef.current = ''
    onVendorCleared()
  }

  useEffect(() => {
    function handler(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isValid = !!resolved
  const similarity = isValid ? matchSimilarity(extractedValue || query, resolved.name) : 0
  const level = matchLevel(similarity)
  const levelClasses = MATCH_LEVEL_CLASSES[level]

  return (
    <div ref={dropRef} className="relative">
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 transition-colors duration-200 ${isValid ? levelClasses.border : (query || '').trim() ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-700'}`}>
        <input
          type="text" value={query} placeholder="Vendor name or NS internal ID…"
          onFocus={() => { if (sortedGroups.top.length > 0 || sortedGroups.bottom.length > 0) setOpen(true) }}
          onChange={e => { setQuery(e.target.value); if (resolved) { setResolved(null); onVendorCleared() } }}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(query); if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none min-w-0"
        />
        {loading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin mr-2 flex-shrink-0" />}
        {isValid && !loading && <button onClick={handleClear} className="p-1.5 mr-1 hover:text-red-500 text-gray-400 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>}
        {!isValid && !loading && (
          <>
            <button onClick={() => doSearch(query)} disabled={query.trim().length < 2}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0 disabled:opacity-40">
              <Search className="h-3.5 w-3.5 text-gray-400" />
            </button>
            <button onClick={() => setOpen(o => !o)} className="p-1.5 mr-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0">
              <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          </>
        )}
      </div>

      {isValid && (
        <p className={`mt-1 text-xs flex items-center gap-1 ${levelClasses.text}`}>
          {level === 'high' ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
          <span className="font-mono font-semibold">ID:{resolved.id}</span>
          <span className="text-gray-400">—</span><span>{resolved.name}</span>
          {level !== 'high' && <span className="font-mono">({Math.round(similarity * 100)}% match)</span>}
        </p>
      )}
      {!isValid && (query || '').trim() && !loading && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          {sortedGroups.top.length > 0 ? 'Not matched — select from dropdown' : 'Press Search (↵) to find NS vendors'}
        </p>
      )}

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-72 overflow-y-auto animate-panel-in">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <div className="flex gap-1.5">
              <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') doSearch(query) }}
                placeholder="Search NS vendors…" autoFocus
                className="flex-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button onClick={() => doSearch(query)} disabled={loading || query.trim().length < 2}
                className="px-2.5 py-1 rounded bg-indigo-600 text-white text-xs font-medium disabled:opacity-40 hover:bg-indigo-700">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Search'}
              </button>
            </div>
          </div>
          {sortedGroups.top.length === 0 && sortedGroups.bottom.length === 0 && !loading && (
            <p className="px-3 py-3 text-xs text-gray-400 italic text-center">Type and search to find NS vendors</p>
          )}
          {sortedGroups.top.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-0.5"><span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide">Best matches</span></div>
              {sortedGroups.top.map(c => (
                <button key={c.id} onClick={() => handleSelect(c)}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between group">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-700 truncate">{c.name}</span>
                  <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">ID:{c.id}</span>
                </button>
              ))}
            </>
          )}
          {sortedGroups.bottom.length > 0 && (
            <>
              <div className={`px-3 pt-2 pb-0.5 ${sortedGroups.top.length > 0 ? 'border-t border-gray-100 dark:border-gray-700 mt-1' : ''}`}>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">All vendors</span>
              </div>
              {sortedGroups.bottom.slice(0, 100).map(c => (
                <button key={c.id} onClick={() => handleSelect(c)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{c.name}</span>
                  <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">ID:{c.id}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── CustomerField — Reseller/Customer resolution, mirrors VendorField ───────
//
// Reseller previously used a bare SearchableDropdown fed by raw search
// results (customerResults/lookups.customers), with no scored/gated
// resolution at all — a real bug (a reseller extracted as "Classroom
// Technologies Pty Ltd" silently posted against an unrelated customer record,
// "GAIA Information Global Ltd") traced to that gap. This mirrors VendorField
// exactly, just against lookup_entity="customers" via the generic
// resolve-reference endpoint instead of resolve-vendor — same near-exact
// auto-accept only, same "keep the extracted text visible, never silently
// swap it" rule, same explicit-click-to-select for anything short of exact.

export function CustomerField({ extractedValue, customers, onCustomerResolved, onCustomerCleared, onCustomerPicked, orgId }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(extractedValue || '')
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(false)
  const [resolved, setResolved] = useState(null) // { id, name }
  const [serverResults, setServerResults] = useState(null)
  const dropRef = useRef(null)
  const hasFetched = useRef(false)
  const lastSearchRef = useRef('')

  useEffect(() => {
    const q = query.trim()
    if (!open || q.length < 2) { setServerResults(null); return }
    let cancelled = false
    const t = setTimeout(() => {
      nsApi.searchCustomers(q)
        .then(res => { if (!cancelled) setServerResults(res.data?.customers || []) })
        .catch(() => { if (!cancelled) setServerResults([]) })
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, open])

  const sortedGroups = useMemo(() => {
    const source = candidates.length > 0
      ? candidates
      : (serverResults && serverResults.length > 0)
        ? serverResults
        : (customers || []).map(c => ({ id: c.id, name: c.name }))
    const scored = source.map(c => ({
      ...c,
      _score: Math.max(fuzzyScore(query, c.name || ''), fuzzyScore(query, String(c.id) || '')),
    }))
    const top = scored.filter(c => c._score > 0).sort((a, b) => b._score - a._score)
    const bottom = scored.filter(c => c._score === 0)
    return { top, bottom }
  }, [candidates, serverResults, customers, query])

  useEffect(() => {
    if (!extractedValue || hasFetched.current) return
    hasFetched.current = true
    setLoading(true)
    nsApi.resolveReference({ lookup_entity: 'customers', extracted_text: extractedValue, org_id: orgId })
      .then(res => {
        const d = res.data || {}
        if (d.matched) {
          // Same rule as VendorField: keep showing the extracted text, the
          // resolved NS name only appears in the confirmation line below.
          setResolved({ id: d.ns_internal_id, name: d.value })
          onCustomerResolved(d.ns_internal_id, d.value)
        }
        // Anything short of the backend's own near-exact match is left
        // unmatched on purpose — no auto-suggested candidate list. A fuzzy
        // "closest guess" (e.g. "BUPA AUSTRALIA HEALTH PTY LTD" -> "Bax
        // Australia Pty Ltd") is worse than nothing: it looks like a real
        // answer sitting right under the field, and the wrong one has
        // already been picked and posted this way. The field stays showing
        // the extracted text with no candidates; a reviewer types their own
        // search below (doSearch) to find and confirm the real record.
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function doSearch(q) {
    const trimmed = q.trim()
    if (trimmed.length < 2 || trimmed === lastSearchRef.current) return
    lastSearchRef.current = trimmed
    setLoading(true)
    try {
      const res = await nsApi.resolveReference({ lookup_entity: 'customers', extracted_text: trimmed, org_id: orgId })
      const d = res.data || {}
      if (d.matched) {
        setResolved({ id: d.ns_internal_id, name: d.value })
        onCustomerResolved(d.ns_internal_id, d.value)
        // A search the user ran themselves — an explicit pick, unlike the
        // on-mount auto-resolve above.
        onCustomerPicked?.(d.ns_internal_id, d.value)
        setOpen(false)
      } else {
        setCandidates(d.candidates || [])
        setOpen(true)
      }
    } catch { /* best effort — fall through */ } finally { setLoading(false) }
  }

  function handleSelect(candidate) {
    setResolved({ id: candidate.id, name: candidate.name })
    setQuery(candidate.name || '')
    setCandidates([])
    setOpen(false)
    onCustomerResolved(candidate.id, candidate.name)
    onCustomerPicked?.(candidate.id, candidate.name)
  }

  function handleClear() {
    setResolved(null); setQuery(extractedValue || ''); setCandidates([]); setServerResults(null); setOpen(false)
    hasFetched.current = false; lastSearchRef.current = ''
    onCustomerCleared()
  }

  useEffect(() => {
    function handler(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isValid = !!resolved
  const similarity = isValid ? matchSimilarity(extractedValue || query, resolved.name) : 0
  const level = matchLevel(similarity)
  const levelClasses = MATCH_LEVEL_CLASSES[level]

  return (
    <div ref={dropRef} className="relative">
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 transition-colors duration-200 ${isValid ? levelClasses.border : (query || '').trim() ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-700'}`}>
        <input
          type="text" value={query} placeholder="Reseller/customer name or NS internal ID…"
          onFocus={() => { if (sortedGroups.top.length > 0 || sortedGroups.bottom.length > 0) setOpen(true) }}
          onChange={e => { setQuery(e.target.value); if (resolved) { setResolved(null); onCustomerCleared() } }}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(query); if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none min-w-0"
        />
        {loading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin mr-2 flex-shrink-0" />}
        {isValid && !loading && <button onClick={handleClear} className="p-1.5 mr-1 hover:text-red-500 text-gray-400 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>}
        {!isValid && !loading && (
          <>
            <button onClick={() => doSearch(query)} disabled={query.trim().length < 2}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0 disabled:opacity-40">
              <Search className="h-3.5 w-3.5 text-gray-400" />
            </button>
            <button onClick={() => setOpen(o => !o)} className="p-1.5 mr-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0">
              <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          </>
        )}
      </div>

      {isValid && (
        <p className={`mt-1 text-xs flex items-center gap-1 ${levelClasses.text}`}>
          {level === 'high' ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
          <span className="font-mono font-semibold">ID:{resolved.id}</span>
          <span className="text-gray-400">—</span><span>{resolved.name}</span>
          {level !== 'high' && <span className="font-mono">({Math.round(similarity * 100)}% match)</span>}
        </p>
      )}
      {!isValid && (query || '').trim() && !loading && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          {sortedGroups.top.length > 0 ? 'Not matched — select from dropdown' : 'Press Search (↵) to find NS customers'}
        </p>
      )}

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-72 overflow-y-auto animate-panel-in">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <div className="flex gap-1.5">
              <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') doSearch(query) }}
                placeholder="Search NS customers…" autoFocus
                className="flex-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button onClick={() => doSearch(query)} disabled={loading || query.trim().length < 2}
                className="px-2.5 py-1 rounded bg-indigo-600 text-white text-xs font-medium disabled:opacity-40 hover:bg-indigo-700">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Search'}
              </button>
            </div>
          </div>
          {sortedGroups.top.length === 0 && sortedGroups.bottom.length === 0 && !loading && (
            <p className="px-3 py-3 text-xs text-gray-400 italic text-center">Type and search to find NS customers</p>
          )}
          {sortedGroups.top.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-0.5"><span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide">Best matches</span></div>
              {sortedGroups.top.map(c => (
                <button key={c.id} onClick={() => handleSelect(c)}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between group">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-700 truncate">{c.name}</span>
                  <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">ID:{c.id}</span>
                </button>
              ))}
            </>
          )}
          {sortedGroups.bottom.length > 0 && (
            <>
              <div className={`px-3 pt-2 pb-0.5 ${sortedGroups.top.length > 0 ? 'border-t border-gray-100 dark:border-gray-700 mt-1' : ''}`}>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">All customers</span>
              </div>
              {sortedGroups.bottom.slice(0, 100).map(c => (
                <button key={c.id} onClick={() => handleSelect(c)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{c.name}</span>
                  <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">ID:{c.id}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── ItemSearchDropdown (NS variant) ─────────────────────────────────────────

export function ItemSearchDropdown({ value, extractedText, items, onChange, matchStatus, placeholder, subsidiaryId, isEditMode = true }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value || extractedText || '')
  const [serverResults, setServerResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [coords, setCoords] = useState(null)
  const boxRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => { if (value) setQuery(value) }, [value])

  // The results panel is portaled to document.body, outside the DOM subtree
  // EditGate's read-only overlay covers — force-close it the moment edit
  // mode turns off so a still-open panel can't be used to change the mapped
  // item after the page is meant to have locked editing.
  useEffect(() => { if (!isEditMode) setOpen(false) }, [isEditMode])

  // The results panel is portaled to document.body (below) rather than
  // rendered inline. Bid Line Items sits inside an `overflow-x-auto` table
  // wrapper (needed for the wide table to scroll horizontally), and per the
  // CSS overflow spec an ancestor with any non-'visible' overflow axis forces
  // the OTHER axis to compute as 'auto' too — so the panel's absolute
  // positioning was being clipped vertically by that wrapper. An
  // absolutely-positioned child can't opt back out of an ancestor's clip
  // region no matter its z-index; only escaping the DOM subtree (a portal)
  // does. This is the same pattern TemplateDetail.jsx's NsFieldSearchDropdown
  // already uses for the identical problem.
  const updateCoords = useCallback(() => {
    if (!boxRef.current) return
    const rect = boxRef.current.getBoundingClientRect()
    const maxPanelHeight = 208 // matches max-h-52 below
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < maxPanelHeight && rect.top > spaceBelow
    setCoords({
      left: rect.left,
      width: rect.width,
      top: openUpward ? null : rect.bottom + 2,
      bottom: openUpward ? window.innerHeight - rect.top + 2 : null,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    updateCoords()
    function handleClickOutside(e) {
      if (
        boxRef.current && !boxRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open, updateCoords])

  // Live NetSuite item search (debounced) — searches ALL items server-side, scoped to
  // the PO subsidiary, so any postable item is findable and returns its internal id.
  // Only runs while the dropdown is OPEN, so it never fires a burst of requests on
  // mount (one per line) that would trip NetSuite's concurrency limit.
  useEffect(() => {
    const q = query.trim()
    // With a subsidiary known, browse its items (blank query) even before typing —
    // without it, unrestricted browsing isn't safe, so require 2+ characters.
    if (!open || (q.length < 2 && !subsidiaryId)) return
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      nsApi.searchItems(q, subsidiaryId)
        .then(res => { if (!cancelled) setServerResults(res.data?.items || []) })
        .catch(() => { if (!cancelled) setServerResults([]) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, open, subsidiaryId])

  // `items` (nsItems) is an unfiltered, capped bulk list with no subsidiary info —
  // it must NEVER be offered as a pickable fallback once a subsidiary is known,
  // or the user can select an item that isn't shared with the PO's subsidiary
  // (NetSuite then rejects it at post time). Only use it as a rough starting
  // list before the vendor/subsidiary is resolved; after that, only NetSuite's
  // own subsidiary-scoped search results (serverResults) are selectable.
  // The two item sources do NOT share a shape: /items/search returns
  // { id, number, name } while the bulk lookups list (get_items) returns
  // { id, itemid, name } — `itemid`, not `number`, and no `description`.
  // Reading it.number/it.description straight off the bulk list therefore
  // produced undefined on every row, which (a) rendered blank, unreadable
  // options and (b) fed setQuery(undefined) on click, making the next
  // query.trim() throw. Normalise to one shape here so both sources behave
  // identically and every field below is guaranteed to be a string.
  const normalisedItems = useMemo(() => (items || []).map(it => ({
    id: it.id,
    number: it.number ?? it.itemid ?? '',
    description: it.description ?? it.name ?? '',
    unitCost: it.unitCost ?? '',
  })), [items])

  const localFiltered = useMemo(() => {
    if (subsidiaryId) return []
    const q = query.trim().toLowerCase()
    if (!q) return normalisedItems.slice(0, 50)
    return normalisedItems
      .map(it => ({ ...it, _s: Math.max(fuzzyScore(q, it.number), fuzzyScore(q, it.description)) }))
      .filter(it => it._s > 0)
      .sort((a, b) => b._s - a._s)
      .slice(0, 50)
  }, [query, normalisedItems, subsidiaryId])

  // Server results win; normalise to { id, number, description, unitCost }.
  const filtered = (serverResults && serverResults.length > 0)
    ? serverResults.map(it => ({ id: it.id, number: it.number, description: it.name || it.description || '', unitCost: it.unitCost || '' }))
    : localFiltered

  const borderClass = matchStatus === 'matched'
    ? 'border-green-400 dark:border-green-600'
    : matchStatus === 'unmatched'
    ? 'border-2 border-amber-400 dark:border-amber-500 hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-700'
    : 'border-gray-300 dark:border-gray-700'
  const tooltipText = matchStatus === 'unmatched'
    ? 'Not matched to a NetSuite item — search by item code or description and select the correct one, or NetSuite will reject this line.'
    : null

  return (
    <FieldTooltip text={tooltipText}>
    <div ref={boxRef} className="relative w-full">
      <div className={`flex items-center border rounded bg-white dark:bg-gray-800 transition-colors duration-200 ${borderClass} ${matchStatus === 'unmatched' ? 'cursor-help' : ''}`}>
        {matchStatus === 'matched' && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1.5 flex-shrink-0" />}
        {matchStatus === 'unmatched' && <AlertTriangle className="h-3 w-3 text-amber-500 ml-1.5 flex-shrink-0" />}
        <input
          type="text" value={query} placeholder={placeholder || 'Item no. or description…'}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-2 py-1.5 text-xs bg-transparent focus:outline-none min-w-0"
        />
        <button onClick={() => setOpen(o => !o)} className="px-1.5 flex-shrink-0">
          <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && coords && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-xl max-h-52 overflow-y-auto animate-panel-in"
          style={{
            top: coords.top != null ? `${coords.top}px` : undefined,
            bottom: coords.bottom != null ? `${coords.bottom}px` : undefined,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
          }}
        >
          {/* Not "Searching NetSuite…" any more: item search is answered from the
              local item cache (ns_item_cache), so this is a local lookup, not a
              live call out to NetSuite. */}
          {loading && <p className="px-3 py-2 text-xs text-gray-400 italic flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</p>}
          {!loading && filtered.length === 0 && <p className="px-3 py-2 text-xs text-gray-400 italic">{query.trim().length < 2 && !subsidiaryId ? 'Type at least 2 characters' : 'No items found'}</p>}
          {filtered.map(it => (
            /* `|| ''` is load-bearing: query feeds query.trim() on the next
                render, so letting an undefined number into state crashes the
                whole view rather than just showing an empty box. */
            <button key={it.id || it.number} onClick={() => { setQuery(it.number || ''); setOpen(false); onChange({ id: it.id, number: it.number, description: it.description, unitCost: it.unitCost }) }}
              className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between">
              <span className="text-xs text-gray-900 dark:text-gray-100 font-medium truncate">{it.description || it.number}</span>
              <span className="ml-2 text-[10px] text-gray-400 font-mono flex-shrink-0">{it.number}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
    </FieldTooltip>
  )
}

// ─── LineItemsTable (NS) ──────────────────────────────────────────────────────

function LineItemsTable({ lines, items, onChange, subsidiaryId, needsReview, reviewReason, isEditMode = true }) {
  function update(idx, field, val) {
    onChange(lines.map((l, i) => i === idx ? { ...l, [field]: val } : l))
  }
  function addLine() {
    onChange([...lines, { lineObjectNumber: '', description: '', quantity: 1, directUnitCost: '', unitOfMeasureCode: '' }])
  }
  function removeLine(idx) { onChange(lines.filter((_, i) => i !== idx)) }

  const lineTotal = lines.reduce((sum, l) => {
    return sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.directUnitCost) || 0)
  }, 0)

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {needsReview && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {reviewReason || 'AI verification disagreed with the extracted line items — please double-check before posting to NetSuite.'}
          </p>
        </div>
      )}
      <div className="overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800" style={{ maxHeight: '400px' }}>
        {lines.length === 0 && (
          <div className="py-8 text-center"><p className="text-sm text-gray-400">No line items yet — add one below</p></div>
        )}
        {lines.map((line, idx) => (
          <div key={idx} className={`px-4 py-3 space-y-2 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Line {idx + 1}</span>
              <button onClick={() => removeLine(idx)} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Item No. <span className="text-red-500">*</span></label>
                <ItemSearchDropdown
                  value={line.lineObjectNumber} extractedText={line._extractedText || ''} items={items} subsidiaryId={subsidiaryId} isEditMode={isEditMode}
                  matchStatus={line.nsItemId && line._matched ? 'matched' : (line._extractedText && !line._matched) ? 'unmatched' : null}
                  onChange={sel => {
                    const next = lines.map((l, i) => i === idx ? { ...l, nsItemId: sel.id, lineObjectNumber: sel.number, description: l.description || sel.description || '', directUnitCost: l.directUnitCost || sel.unitCost || '', _matched: true } : l)
                    onChange(next)
                  }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Description</label>
                <input type="text" value={line.description} onChange={e => update(idx, 'description', e.target.value)} placeholder="Item description"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Quantity <span className="text-red-500">*</span></label>
                <input type="number" min="0" step="any" value={line.quantity} onChange={e => update(idx, 'quantity', e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 text-right" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Unit Cost</label>
                <input type="number" min="0" step="any" value={line.directUnitCost} onChange={e => update(idx, 'directUnitCost', e.target.value)} placeholder="0.00"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 text-right" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">UOM</label>
                <input type="text" value={line.unitOfMeasureCode} onChange={e => update(idx, 'unitOfMeasureCode', e.target.value)} placeholder="EA"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 uppercase" />
              </div>
            </div>
            {(parseFloat(line.quantity) || 0) > 0 && (parseFloat(line.directUnitCost) || 0) > 0 && (
              <p className="text-right text-[11px] text-gray-400">
                Subtotal: <span className="font-medium text-gray-600 dark:text-gray-300">
                  {((parseFloat(line.quantity) || 0) * (parseFloat(line.directUnitCost) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button onClick={addLine} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-indigo-400 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add Line
        </button>
        {lineTotal > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total:&nbsp;<span className="font-semibold text-gray-900 dark:text-gray-100">
              {lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </p>
        )}
      </div>
    </div>
  )
}

// ─── NS-specific fields: vendor fields that map to ns_field ──────────────────

const VENDOR_FIELD_KEYS = new Set(['entity', 'vendorName', 'vendorId'])
const ARRAY_FIELD_TYPES = new Set(['array_of_objects', 'table'])
// currency/terms are vendor-derived like subsidiary/location (shown via the dedicated currency dropdown, not as a raw text field)
const NS_AUTO_FIELDS = new Set(['subsidiary', 'location', 'custbody16', 'currency', 'terms']) // handled separately via dedicated dropdowns
// Fields NetSuite sets itself on a PO — never send extracted values for them:
//  • currency / terms are derived from the vendor (sending a mismatch → "Invalid Field Value")
//  • total / subtotal / tax are read-only, computed from the line items (extracted value is often wrong, e.g. "-2")
const NS_DO_NOT_SEND = new Set(['currency', 'terms', 'total', 'subtotal', 'taxtotal', 'tax_total', 'discounttotal', 'nettotal'])

// ─── Payload projection ───────────────────────────────────────────────────────
// Single source of truth for turning on-screen state into an NS payload. Used by
// the local validator, the pre-post server validation, and the post itself.
//
// This has to be shared: if the local engine validated a differently-shaped
// payload than the one actually posted, the two would disagree about *projection*
// rather than about rules — a divergence no parity test on the rules could catch.

function buildNsHeaderPayload({
  headerFields, fieldValues, vendorResolved, vendorNsId,
  selectedSubsidiary, selectedLocation, selectedShippingTerms,
}) {
  const payload = {}
  for (const f of headerFields) {
    if (!f.ns_field) continue
    if (NS_DO_NOT_SEND.has(f.ns_field.toLowerCase())) continue
    payload[f.ns_field] = fieldValues[f.id] ?? ''
  }
  if (vendorResolved && vendorNsId) payload.entity = vendorNsId
  if (selectedSubsidiary) payload.subsidiary = selectedSubsidiary.id
  if (selectedLocation) payload.location = selectedLocation.id
  // currency/terms are NOT sent — NetSuite derives them from the vendor.
  if (selectedShippingTerms) payload.custbody16 = selectedShippingTerms.id
  return payload
}

function buildNsLinePayload(lineItems) {
  return (lineItems || [])
    .filter(l => l.nsItemId || l.description)
    .map(l => ({
      item: l.nsItemId || '',
      description: l.description || '',
      quantity: parseFloat(l.quantity) || 0,
      rate: parseFloat(l.directUnitCost) || 0,
    }))
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NSRequestView({ request, showProcessingModal, processingMinimized, setProcessingMinimized, processingProgress = { percent: 0, message: '' }, processingLog = [], onOpCoMoved }) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const { showConfidenceScore, showAiBadge, showColorCoding } = useDisplayPreferences()
  const isReader = isReadOnlyUser(authUser)
  const isEditable = !isReader && !isRequestLocked(request?.status)
  const notifyLocked = () => toast.info('Read-only', isReader ? 'You have read-only access' : 'This request is finalized and can no longer be edited')

  const fields = useMemo(() =>
    (request?.fields || []).filter(f => f.is_active !== false).map(f => ({
      ...f,
      value: f.value ?? f.field_value ?? '',
      display_name: f.display_name || f.template_field?.display_name || f.field_name || '',
      ns_field: f.ns_field ?? f.template_field?.ns_field ?? null,
      is_required: f.is_required || f.template_field?.is_required || false,
      field_type: f.field_type || f.template_field?.field_type || f.template_field?.data_type || 'text',
    }))
  , [request])

  const headerFields = useMemo(() => fields.filter(f => !ARRAY_FIELD_TYPES.has(f.field_type)), [fields])

  // The line-item table field itself — used both to parse rows and to surface
  // the needs_review flag set by backend CU/LLM line-item reconciliation.
  const lineItemTableField = useMemo(() => fields.find(f =>
    ARRAY_FIELD_TYPES.has(f.field_type) ||
    ARRAY_FIELD_TYPES.has(f.template_field?.field_type) ||
    ARRAY_FIELD_TYPES.has(f.template_field?.data_type) ||
    (f.value && typeof f.value === 'string' && f.value.trimStart().startsWith('[') &&
      (f.field_name?.toLowerCase().includes('line') || f.field_name?.toLowerCase().includes('item')))
  ), [fields])

  // Parse line items
  const extractedLines = useMemo(() => {
    const tableField = lineItemTableField
    if (!tableField?.value) return []
    try {
      const raw = typeof tableField.value === 'string' ? JSON.parse(tableField.value) : tableField.value
      if (!Array.isArray(raw)) return []
      return raw.map(rawRow => {
        const row = Object.fromEntries(Object.entries(rawRow).map(([k, v]) => [k.toLowerCase().trim().replace(/[\s.-]+/g, '_'), v]))
        const rowOrig = Object.fromEntries(Object.entries(rawRow).map(([k, v]) => [k.toLowerCase().trim(), v]))
        const pick = (...keys) => { for (const k of keys) { if (row[k] !== undefined && row[k] !== '') return row[k]; if (rowOrig[k] !== undefined && rowOrig[k] !== '') return rowOrig[k] } return '' }
        return {
          lineObjectNumber: pick('item_code', 'code', 'number', 'item_no', 'item_number', 'sku', 'part_number'),
          // Supplier/vendor item code — this is what matches the NetSuite itemid.
          _supplierCode:    pick('supplier_item_code', 'supplier_item_cod', 'supplier_code', 'vendor_code', 'vendor_item_code', 'manufacturer_code', 'mpn'),
          description:      pick('description', 'item_description', 'product', 'name', 'desc'),
          quantity:         parseFloat(pick('quantity', 'qty', 'ordered_qty') || 1) || 1,
          directUnitCost:   parseFloat(pick('unit_price', 'price', 'unit_cost', 'rate', 'unit_amount') || 0) || '',
          unitOfMeasureCode: pick('uom', 'unit', 'unit_of_measure'),
        }
      })
    } catch { return [] }
  }, [fields])

  const [lineItems, setLineItems] = useState(() =>
    extractedLines.length > 0 ? extractedLines : [{ lineObjectNumber: '', description: '', quantity: 1, directUnitCost: '', unitOfMeasureCode: '' }]
  )

  const extractedLinesLoaded = useRef(false)
  useEffect(() => {
    if (extractedLines.length > 0 && !extractedLinesLoaded.current) {
      extractedLinesLoaded.current = true
      setLineItems(extractedLines)
    }
  }, [extractedLines])

  const [fieldValues, setFieldValues] = useState(() => {
    const init = {}
    for (const f of headerFields) init[f.id] = normaliseForInput(f.value, getInputType(f))
    return init
  })

  const fieldsSynced = useRef(false)
  useEffect(() => {
    if (headerFields.length === 0 || fieldsSynced.current) return
    fieldsSynced.current = true
    setFieldValues(prev => {
      const next = { ...prev }; let changed = false
      for (const f of headerFields) {
        const incoming = normaliseForInput(f.value, getInputType(f))
        if (incoming && !prev[f.id]) { next[f.id] = incoming; changed = true }
      }
      return changed ? next : prev
    })
  }, [headerFields])

  // ── NS Lookups ──
  const [lookups, setLookups] = useState({})
  const [lookupsLoading, setLookupsLoading] = useState(true)
  const [lookupsError, setLookupsError] = useState('')

  // The screen's single initialisation call. Passing the template id makes the
  // response carry `validation` — the rule bundle the local validator runs on —
  // so dropdown data and validation rules arrive together in one request (AC2).
  //
  // One automatic retry: a transient failure here would otherwise drop the whole
  // form into baseline-rules mode for the rest of the session.
  const templateId = request?.template_id
  useEffect(() => {
    let cancelled = false
    let retryTimer = null

    const load = (isRetry) => nsApi.getLookups(templateId)
      .then(res => {
        if (cancelled) return
        setLookups(res.data || {})
        setLookupsError('')
        setLookupsLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        if (!isRetry) {
          retryTimer = setTimeout(() => load(true), 2000)
          return
        }
        setLookupsError(err?.response?.data?.error || 'Unable to load NetSuite lookups. Check the connection and role permissions.')
        setLookupsLoading(false)
      })

    load(false)
    return () => { cancelled = true; clearTimeout(retryTimer) }
  }, [templateId])

  const reloadLookups = useCallback(() => {
    setLookupsLoading(true)
    return nsApi.getLookups(templateId)
      .then(res => { setLookups(res.data || {}); setLookupsError('') })
      .catch(err => setLookupsError(err?.response?.data?.error || 'Unable to load NetSuite lookups.'))
      .finally(() => setLookupsLoading(false))
  }, [templateId])

  // NS subsidiaries, locations, currencies, shipping terms
  const subsidiaries = useMemo(() => lookups.subsidiaries || [], [lookups])
  const currencies = useMemo(() => lookups.currencies || [], [lookups])
  const allLocations = useMemo(() => lookups.locations || [], [lookups])
  const shippingTermsList = useMemo(() => lookups.shipping_terms || [], [lookups])

  // ── NS-specific: Subsidiary + Location + Currency ──
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedCurrency, setSelectedCurrency] = useState(null)
  const [selectedShippingTerms, setSelectedShippingTerms] = useState(null)

  useEffect(() => {
    if (lookupsLoading) return
    const subsidiaryId = request?.ns_default_subsidiary_id || request?.template?.ns_default_subsidiary_id
    const locationId = request?.ns_default_location_id || request?.template?.ns_default_location_id
    const currencyId = request?.ns_default_currency_id || request?.template?.ns_default_currency_id
    if (subsidiaryId && !selectedSubsidiary) {
      setSelectedSubsidiary(subsidiaries.find(o => String(o.id) === String(subsidiaryId)) || null)
    }
    if (locationId && !selectedLocation) {
      setSelectedLocation(allLocations.find(o => String(o.id) === String(locationId)) || null)
    }
    if (currencyId && !selectedCurrency) {
      setSelectedCurrency(currencies.find(o => String(o.id) === String(currencyId)) || null)
    }
    // Auto-select shipping terms if a custbody16 value exists in extracted fields
    if (!selectedShippingTerms && shippingTermsList.length > 0) {
      const cb16Field = headerFields.find(f => f.ns_field === 'custbody16')
      const cb16Val = cb16Field ? (fieldValues[cb16Field.id] || cb16Field.value) : null
      if (cb16Val) {
        const match = shippingTermsList.find(o => String(o.id) === String(cb16Val) || (o.name && o.name.toLowerCase() === String(cb16Val).toLowerCase()))
          || shippingTermsList.find(o => fuzzyScore(String(cb16Val), o.name || '') >= 0.7)
        if (match) setSelectedShippingTerms(match)
      }
    }
    // Auto-select currency if a 'currency' value was extracted from the document
    // (e.g. "AUD") — without this it silently never gets matched even when the
    // AI found it right there on the PO.
    if (!selectedCurrency && currencies.length > 0) {
      const currencyField = headerFields.find(f => f.ns_field === 'currency')
      const currencyVal = currencyField ? (fieldValues[currencyField.id] || currencyField.value) : null
      if (currencyVal) {
        const match = currencies.find(o => String(o.id) === String(currencyVal) || (o.name && o.name.toLowerCase() === String(currencyVal).toLowerCase()) || (o.symbol && o.symbol.toLowerCase() === String(currencyVal).toLowerCase()))
          || currencies.find(o => fuzzyScore(String(currencyVal), o.name || '') >= 0.7)
        if (match) setSelectedCurrency(match)
      }
    }
  }, [lookupsLoading, request, subsidiaries, allLocations, currencies, shippingTermsList, headerFields, fieldValues, selectedSubsidiary, selectedLocation, selectedCurrency, selectedShippingTerms])

  // Raw extracted text for fields shown via dedicated dropdowns rather than a
  // plain input — used to flag "extracted but unmatched" with an amber border
  // instead of silently dropping the value the AI found on the document.
  const extractedCurrencyText = useMemo(() => {
    const f = headerFields.find(f => f.ns_field === 'currency')
    const v = f ? (fieldValues[f.id] || f.value) : null
    return v ? String(v).trim() : ''
  }, [headerFields, fieldValues])

  const extractedShippingTermsText = useMemo(() => {
    const f = headerFields.find(f => f.ns_field === 'custbody16')
    const v = f ? (fieldValues[f.id] || f.value) : null
    return v ? String(v).trim() : ''
  }, [headerFields, fieldValues])

  // Filter locations by selected subsidiary
  const filteredLocations = useMemo(() => {
    if (!selectedSubsidiary) return allLocations
    // Backend returns every subsidiary a location is scoped to in `subsidiaries`
    // (from locationsubsidiarymap + the scalar column). A location with NO
    // subsidiaries at all is truly shared/unrestricted and valid for any subsidiary.
    return allLocations.filter(loc => {
      const subs = Array.isArray(loc.subsidiaries) ? loc.subsidiaries : (loc.subsidiary ? [loc.subsidiary] : [])
      return subs.length === 0 || subs.map(String).includes(String(selectedSubsidiary.id))
    })
  }, [allLocations, selectedSubsidiary])

  // When subsidiary changes, reset location if it's no longer valid
  useEffect(() => {
    if (selectedLocation && selectedSubsidiary) {
      const stillValid = filteredLocations.some(l => String(l.id) === String(selectedLocation.id))
      if (!stillValid) setSelectedLocation(null)
    }
  }, [selectedSubsidiary, filteredLocations])

  // NS items
  const nsItems = useMemo(() => {
    const raw = lookups.items || []
    return raw.map(it => ({
      // id = NetSuite internal id (this is what gets POSTed as the line item).
      id: String(it.id ?? it.internalId ?? ''),
      // number = human item code / SKU (itemid) — used for matching and display, NOT sent.
      number: it.itemid || it.number || it.sku || String(it.id ?? ''),
      description: it.name || it.description || '',
      unitCost: it.rate || it.unitCost || '',
    }))
  }, [lookups])

  // Auto-resolve line items against NetSuite.
  // Matches on supplier item code or item number (case-insensitive) against NS itemid.
  //
  // Once a subsidiary is known, this MUST go through the server-side resolveItems
  // call (SQL-joined against itemsubsidiarymap) rather than the bulk `nsItems`
  // catalog, which has NO subsidiary information at all. Matching against the
  // unfiltered catalog would happily mark a line "_matched: true" (green check)
  // for an item that isn't shared with the vendor's subsidiary — NetSuite then
  // rejects the whole PO at post time with a subsidiary/item mismatch error that
  // the user never saw coming. The subsidiary-scoped lookup catches that here,
  // before posting, so an unresolvable line surfaces as amber "not matched"
  // instead of a failed post.
  const lastResolvedSub = useRef(undefined)
  useEffect(() => {
    const src = extractedLines
    if (!src.length) return
    if (!src.some(l => l.description || l.lineObjectNumber || l._supplierCode)) return
    const subId = selectedSubsidiary?.id ?? null
    if (lastResolvedSub.current === subId) return
    lastResolvedSub.current = subId

    const codesFor = l => [l._supplierCode, l.lineObjectNumber].filter(Boolean).map(c => String(c).trim())

    let cancelled = false

    // resolvedForSub records WHICH subsidiary this resolution was scoped to, or
    // null for the unscoped bulk-catalog preview. That provenance is what lets the
    // browser answer "is this item valid for the current subsidiary?" without a
    // request: resolveItems/searchItems are already subsidiary-scoped and the item
    // dropdown offers nothing else once a subsidiary is known, so an id obtained
    // under the current subsidiary is shared with it by construction. Shipping
    // itemsubsidiarymap to the client is not an option — it is items × subsidiaries.
    const applyResolved = (resolvedByCode, resolvedForSub) => setLineItems(prev => src.map((l, i) => {
      const cur = prev[i] || l
      const base = { ...l, quantity: cur.quantity ?? l.quantity, directUnitCost: cur.directUnitCost ?? l.directUnitCost, description: cur.description || l.description }
      const extractedText = l._supplierCode || l.lineObjectNumber || ''
      const hit = codesFor(l).map(c => resolvedByCode[c.toLowerCase()]).find(Boolean)
      if (hit) {
        return {
          ...base, nsItemId: hit.id, lineObjectNumber: hit.number,
          description: base.description || hit.description,
          _matched: true, _extractedText: extractedText,
          _resolvedForSubsidiary: resolvedForSub,
        }
      }
      return {
        ...base, nsItemId: '', _matched: false, _extractedText: extractedText,
        _resolvedForSubsidiary: null,
      }
    }))

    if (subId) {
      // Subsidiary known — resolve authoritatively via NetSuite (subsidiary-scoped).
      const codes = Array.from(new Set(src.flatMap(codesFor)))
      if (codes.length === 0) return
      nsApi.resolveItems(codes, subId)
        .then(res => {
          if (cancelled) return
          const resolved = res.data?.resolved || {}
          applyResolved(resolved, String(subId))
        })
        .catch(() => {
          if (cancelled) return
          setLineItems(prev => src.map((l, i) => ({
            ...(prev[i] || l), nsItemId: '', _matched: false,
            _extractedText: l._supplierCode || l.lineObjectNumber || '',
            _resolvedForSubsidiary: null,
          })))
        })
    } else if (nsItems.length > 0) {
      // No subsidiary yet — quick client-side preview against the bulk (unscoped)
      // catalog so the user sees a first pass; it gets re-resolved authoritatively
      // the moment a subsidiary is selected (subId above becomes non-null).
      const byNumber = {}
      for (const it of nsItems) {
        if (it.number) byNumber[it.number.toLowerCase().trim()] = it
      }
      // null provenance: the bulk catalog carries no subsidiary information, so
      // these matches are a preview only and stay flagged as unverified.
      applyResolved(byNumber, null)
    }

    return () => { cancelled = true }
  }, [extractedLines, selectedSubsidiary, nsItems])

  // ── Vendor state ──
  const [vendorResolved, setVendorResolved] = useState(false)
  const [vendorNsId, setVendorNsId] = useState('')
  const [vendorNsName, setVendorNsName] = useState('')
  // The vendor's own subsidiary. Once known, the PO subsidiary is LOCKED to it —
  // a PO can only belong to the vendor's subsidiary, and never the (non-postable)
  // root subsidiary. This keeps subsidiary → location → items on one consistent company.
  const [vendorSubsidiaryId, setVendorSubsidiaryId] = useState(null)

  function handleVendorResolved(id, name, subsidiaryHint) {
    setVendorResolved(true); setVendorNsId(id); setVendorNsName(name)
    setFieldValues(prev => {
      const next = { ...prev }
      for (const f of headerFields) {
        if (!f.ns_field) continue
        if (VENDOR_FIELD_KEYS.has(f.ns_field)) next[f.id] = String(id)
      }
      return next
    })
    // Auto-select the subsidiary the vendor belongs to. NetSuite (OneWorld) rejects
    // a PO whose subsidiary doesn't match the vendor's ("Invalid Field Value N for
    // field: subsidiary"). Prefer the hint carried on the selected vendor; otherwise
    // resolve it from the vendors lookup by internal id.
    let vendorSubId = subsidiaryHint
    if (vendorSubId == null || vendorSubId === '') {
      const vendor = (lookups.vendors || []).find(v => String(v.id ?? v.internalId) === String(id))
      vendorSubId = vendor && (vendor.subsidiary ?? vendor.subsidiary_id)
    }
    if (vendorSubId != null && vendorSubId !== '') {
      setVendorSubsidiaryId(String(vendorSubId))
      const match = subsidiaries.find(s => String(s.id) === String(vendorSubId))
      // Force it — override any template default or earlier (possibly root) selection.
      setSelectedSubsidiary(match || { id: String(vendorSubId), name: `Subsidiary ${vendorSubId}` })
    } else {
      setVendorSubsidiaryId(null)
    }
  }

  function handleVendorCleared() {
    setVendorResolved(false); setVendorNsId(''); setVendorNsName('')
    setVendorSubsidiaryId(null)
    setFieldValues(prev => {
      const next = { ...prev }
      for (const f of headerFields) { if (VENDOR_FIELD_KEYS.has(f.ns_field)) next[f.id] = '' }
      return next
    })
  }

  // Once a vendor is chosen, restrict the Subsidiary dropdown to the vendor's own
  // subsidiary so it can't be set to a mismatched or non-postable (root) subsidiary.
  const subsidiaryOptions = useMemo(() => {
    if (!vendorSubsidiaryId) return subsidiaries
    const scoped = subsidiaries.filter(s => String(s.id) === String(vendorSubsidiaryId))
    return scoped.length > 0 ? scoped : subsidiaries
  }, [subsidiaries, vendorSubsidiaryId])

  // ── Local-first validation ──
  // Rules arrive once, inside the /lookups bootstrap, and are evaluated in the
  // browser on every change. No request is made while the user types (issue #291):
  // the server's /validate is still called immediately before posting, where it
  // adds the live NetSuite checks (vendor active, item↔subsidiary) that cannot be
  // answered locally, and remains the authoritative gate.
  //
  // The bundle is compiled once per fetch, not per keystroke — compileBundle
  // builds the Sets and the RegExp that keep evaluation inside one frame.
  const compiledRules = useMemo(
    () => compileBundle(lookups.validation || NS_BASELINE_BUNDLE),
    [lookups.validation],
  )

  // True once the bootstrap has settled without delivering rules. Drives the
  // degraded-mode banner; baseline rules are a strict subset of a real bundle, so
  // this can only ever under-report, never invent a blocker (AC4).
  const rulesDegraded = !lookupsLoading && !lookups.validation

  const nsHeaderPayload = useMemo(
    () => buildNsHeaderPayload({
      headerFields, fieldValues, vendorResolved, vendorNsId,
      selectedSubsidiary, selectedLocation, selectedShippingTerms,
    }),
    [headerFields, fieldValues, vendorResolved, vendorNsId,
     selectedSubsidiary, selectedLocation, selectedShippingTerms],
  )

  const nsLinePayload = useMemo(() => buildNsLinePayload(lineItems), [lineItems])

  const localErrors = useMemo(
    () => validateNsPayload(compiledRules, nsHeaderPayload, nsLinePayload).errors,
    [compiledRules, nsHeaderPayload, nsLinePayload],
  )

  // Errors that only the server can produce — the live NetSuite layers (vendor
  // active, item↔subsidiary) reported by the pre-post /validate call. Held
  // separately from localErrors, which is derived and cannot be assigned to.
  const [serverErrors, setServerErrors] = useState([])

  // Any edit invalidates a previous server verdict: keeping it on screen after the
  // user has changed the offending field would show a stale blocker they have
  // already fixed. The next post attempt re-runs the server check anyway.
  useEffect(() => {
    setServerErrors(prev => (prev.length ? [] : prev))
  }, [nsHeaderPayload, nsLinePayload])

  const validationErrors = useMemo(() => {
    if (!serverErrors.length) return localErrors
    const seen = new Set(localErrors.map(e => `${e.field_key}|${e.message}`))
    return [
      ...localErrors,
      ...serverErrors.filter(e => !seen.has(`${e.field_key}|${e.message}`)),
    ]
  }, [localErrors, serverErrors])

  const persistReviewState = useCallback(async () => {
    const fieldsToSave = []

    for (const f of headerFields) {
      const nextVal = fieldValues[f.id] ?? ''
      fieldsToSave.push({
        id: f.id,
        field_name: f.field_name || f.display_name || f.ns_field || `field_${f.id}`,
        field_value: nextVal,
        template_field_id: f.template_field_id || f.template_field?.id || null,
        extracted_value: f.extracted_value ?? f.value ?? '',
        normalized_value: f.normalized_value ?? nextVal,
        is_active: f.is_active !== false,
        is_selected: f.is_selected !== false,
        source_type: f.source_type || 'user_input',
      })
    }

    const lineValue = lineItems
      .filter(l => l.lineObjectNumber || l.description)
      .map(l => ({
        item_code: l.lineObjectNumber || '',
        description: l.description || '',
        quantity: parseFloat(l.quantity) || 0,
        unit_price: l.directUnitCost !== '' && l.directUnitCost != null ? (parseFloat(l.directUnitCost) || 0) : null,
        uom: l.unitOfMeasureCode || '',
      }))

    if (lineValue.length > 0) {
      const existingLineField = fields.find(f =>
        ARRAY_FIELD_TYPES.has((f.field_type || '').toLowerCase()) ||
        ARRAY_FIELD_TYPES.has((f.template_field?.field_type || '').toLowerCase()) ||
        ARRAY_FIELD_TYPES.has((f.template_field?.data_type || '').toLowerCase())
      )

      if (existingLineField?.id) {
        fieldsToSave.push({
          id: existingLineField.id,
          field_name: existingLineField.field_name || 'line_items',
          field_value: JSON.stringify(lineValue),
          template_field_id: existingLineField.template_field_id || existingLineField.template_field?.id || null,
          extracted_value: existingLineField.extracted_value ?? existingLineField.value ?? '',
          normalized_value: JSON.stringify(lineValue),
          is_active: existingLineField.is_active !== false,
          is_selected: existingLineField.is_selected !== false,
          source_type: existingLineField.source_type || 'user_input',
        })
      } else {
        const templateLineField = (request?.template?.fields || []).find(tf =>
          ARRAY_FIELD_TYPES.has((tf.field_type || '').toLowerCase())
        )
        if (templateLineField) {
          fieldsToSave.push({
            field_name: templateLineField.field_name,
            field_value: JSON.stringify(lineValue),
            template_field_id: templateLineField.id,
            extracted_value: '',
            normalized_value: JSON.stringify(lineValue),
            is_active: true,
            is_selected: true,
            source_type: 'user_input',
          })
        }
      }
    }

    if (fieldsToSave.length === 0) return
    await requestsApi.updateFields(request.id, fieldsToSave, 'Saved from NetSuite review before posting')
  }, [headerFields, fieldValues, lineItems, fields, request?.template?.fields, request?.id])

  // The 800 ms debounced nsApi.validate() effect that used to live here is gone:
  // validationErrors is now derived synchronously by the local engine above, so
  // there is nothing to debounce and no request to fire while typing.

  // ── Post ──
  const [posting, setPosting] = useState(false)
  const [postResult, setPostResult] = useState(() =>
    request?.ns_post_status === 'posted' || request?.ns_po_number
      ? { success: true, po_number: request.ns_po_number, ns_url: request.ns_url }
      : null
  )
  const [postAttempts, setPostAttempts] = useState(request?.ns_post_attempts || 0)
  const [postProgress, setPostProgress] = useState({ percent: 0, message: '' })
  const [postMinimized, setPostMinimized] = useState(false)
  const [pendingApproval, setPendingApproval] = useState(false)
  const MAX_ATTEMPTS = 3

  async function handlePost() {
    if (posting) return
    setPosting(true); setPostResult(null); setPostMinimized(false)
    setPostProgress({ percent: 5, message: 'Preparing fields…' })
    // Same projection the local validator ran on, so what was validated on screen
    // is exactly what gets validated server-side and posted.
    const hf = nsHeaderPayload
    const cleanLines = lineItems
      .filter(l => l.nsItemId && (parseFloat(l.quantity) || 0) > 0)
      .map(l => ({
        item: l.nsItemId,
        description: l.description || '',
        quantity: parseFloat(l.quantity),
        ...(l.directUnitCost !== '' && l.directUnitCost != null ? { rate: parseFloat(l.directUnitCost) || 0 } : {}),
      }))
    try {
      // Persist user-reviewed values so reopening the request shows the same data.
      await persistReviewState()

      setPostProgress({ percent: 25, message: 'Validating against NetSuite…' })
      const vRes = await nsApi.validate({ request_id: request.id, org_id: request.organization_id, payload: hf, line_items: cleanLines })
      if (!vRes.data?.valid) {
        setServerErrors(vRes.data?.errors || [])
        setPosting(false); setPostProgress({ percent: 0, message: '' }); return
      }
      setPostProgress({ percent: 55, message: 'Creating Purchase Order in NetSuite…' })
      const res = await nsApi.createPurchaseOrder({ request_id: request.id, org_id: request.organization_id, header_fields: hf, line_items: cleanLines })
      const d = res.data || {}
      if (d.success) {
        setPostProgress({ percent: 90, message: 'Finalising and saving…' })
        await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
        setPostProgress({ percent: 100, message: 'Purchase Order created!' })
        // NS-specific: show pending approval banner if supervisor approval required
        if (d.pending_approval) setPendingApproval(true)
        setTimeout(() => {
          setPosting(false)
          setPostResult({ success: true, po_number: d.po_number || d.ns_po_number, ns_url: d.ns_url })
          setPostAttempts(0)
        }, 800)
        toast.success('Posted to NetSuite', `Purchase Order ${d.po_number || d.ns_po_number} created successfully.`)
      } else {
        const attempt = postAttempts + 1; setPostAttempts(attempt)
        setPosting(false); setPostProgress({ percent: 0, message: '' })
        setPostResult({ success: false, error: d.error || 'Unknown error', attempt })
      }
    } catch (err) {
      const attempt = postAttempts + 1; setPostAttempts(attempt)
      setPosting(false); setPostProgress({ percent: 0, message: '' })
      setPostResult({ success: false, error: err?.response?.data?.error || 'Request failed', attempt })
    }
  }

  async function handleRetry() {
    if (posting || postAttempts >= MAX_ATTEMPTS) return
    setPosting(true)
    try {
      const res = await nsApi.retryPost({ request_id: request.id, org_id: orgId })
      const d = res.data || {}
      if (d.success) {
        setPostResult({ success: true, po_number: d.po_number, ns_url: d.ns_url }); setPostAttempts(0)
        await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
        toast.success('Retried successfully', `PO ${d.po_number} created.`)
      } else {
        const attempt = d.attempt || postAttempts + 1; setPostAttempts(attempt)
        setPostResult({ success: false, error: d.error || 'Retry failed', attempt })
      }
    } catch (err) {
      const attempt = postAttempts + 1; setPostAttempts(attempt)
      setPostResult({ success: false, error: err?.response?.data?.error || 'Retry failed', attempt })
    } finally { setPosting(false) }
  }

  const validLineCount = lineItems.filter(l => l.nsItemId).length

  // Lines whose item was NOT resolved under the currently-selected subsidiary.
  // Replaces the old per-keystroke server round trip for the item↔subsidiary check
  // (Layer 3b), which is the one layer that cannot be evaluated locally. Reported
  // as a SOFT warning, never a blocker: the resolve effect re-resolves whenever the
  // subsidiary changes, so this state is transient, and the pre-post server
  // validation still performs the authoritative itemsubsidiarymap check.
  const unverifiedForSubsidiaryLines = selectedSubsidiary
    ? lineItems.filter(l =>
        l.nsItemId &&
        String(l._resolvedForSubsidiary ?? '') !== String(selectedSubsidiary.id))
    : []
  const missingRequiredFields = headerFields.filter(f => {
    if (VENDOR_FIELD_KEYS.has(f.ns_field)) return false
    if (NS_AUTO_FIELDS.has(f.ns_field)) return false
    if ((f.ns_field || '').toLowerCase() === 'trandate') return false // reported separately as missingTranDate
    return f.is_required && !String(fieldValues[f.id] ?? '').trim()
  })
  const missingSubsidiary = !selectedSubsidiary
  // Location is marked required (*) in the UI but was never actually gating the
  // Approve/Post button — a request could reach NetSuite with no location set,
  // fail server-side, and burn a post attempt before the user ever saw the error.
  const missingLocation = !selectedLocation
  // Shipping Terms (custbody16) is enforced by a custom workflow/script on this
  // account — confirmed by directly posting a minimal PO and reading NetSuite's
  // own rejection — so gate on it here too, same as Location above.
  const missingShippingTerms = !selectedShippingTerms
  // NetSuite always requires a transaction date on a PO (_ACCOUNT_REQUIRED_FIELDS
  // on the backend), regardless of whether this template's field config happens
  // to have is_required set on it — gate on it here too so it can't slip through.
  const tranDateField = headerFields.find(f => (f.ns_field || '').toLowerCase() === 'trandate')
  const missingTranDate = !!tranDateField && !String(fieldValues[tranDateField.id] ?? '').trim()

  // ── Jump-to-field: scroll + pulse-highlight a field (or vendor / subsidiary /
  // location / shipping terms / line items section) when a checklist row is
  // clicked. Same mechanism as the Business Central request page. ──
  const fieldRowRefs = useRef({})
  const [pulsedFieldKey, setPulsedFieldKey] = useState(null)
  const fieldJumpCycleRef = useRef({})

  const resolveFieldKey = (nsFieldOrName) => {
    if (!nsFieldOrName) return null
    const key = String(nsFieldOrName).toLowerCase()
    if (key === 'entity') return 'vendor'
    if (key === 'subsidiary') return 'subsidiary'
    if (key === 'location') return 'location'
    if (key === 'custbody16') return 'shippingTerms'
    if (key.startsWith('line_')) return 'lines'
    const f = headerFields.find(hf =>
      hf.ns_field === nsFieldOrName || hf.field_name === nsFieldOrName || hf.display_name === nsFieldOrName
    )
    return f ? `field-${f.id}` : null
  }

  const jumpToFieldKeys = (keys) => {
    const validKeys = (keys || []).filter(Boolean)
    if (validKeys.length === 0) return
    const groupId = validKeys.join('|')
    const idx = (fieldJumpCycleRef.current[groupId] || 0) % validKeys.length
    fieldJumpCycleRef.current[groupId] = idx + 1
    const key = validKeys[idx]
    const el = fieldRowRefs.current[key]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setPulsedFieldKey(key)
    setTimeout(() => setPulsedFieldKey(k => (k === key ? null : k)), 1800)
  }

  const jumpHighlightClass = (key) =>
    pulsedFieldKey === key
      ? 'rounded-lg ring-2 ring-amber-400 dark:ring-amber-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 animate-pulse'
      : ''

  // Per-field-blank mandatory errors are already covered individually below
  // (vendor/subsidiary/location/shipping-terms/date have their own dedicated
  // rows, and missingRequiredFields covers every other required header field) —
  // so only surface OTHER local-validator findings here (date-format errors,
  // and anything else future rule layers add) to avoid a duplicate "required"
  // row for the same field.
  const DEDICATED_FIELD_KEYS = new Set(['entity', 'subsidiary', 'location', 'custbody16', 'trandate'])
  const extraHeaderIssues = validationErrors.filter(e => {
    const key = (e.field_key || '').toLowerCase()
    return e.layer !== 'mandatory' && !key.startsWith('line_') && !DEDICATED_FIELD_KEYS.has(key)
  })

  // One row per line that isn't postable yet — matched item + a quantity > 0 —
  // built directly off lineItems (not the local validator) so a raw extracted
  // item code with no description or nsItemId still gets caught; the validator
  // silently drops such a row before it ever reaches NetSuite's field checks.
  const lineIssueRows = lineItems.flatMap((l, idx) => {
    if (!(l.lineObjectNumber || l.description)) return [] // blank row, nothing to report
    const rows = []
    if (!l.nsItemId) rows.push({ label: `Line ${idx + 1}`, detail: 'Not matched to a NetSuite item', group: 'Line Items', keys: ['lines'] })
    if ((parseFloat(l.quantity) || 0) <= 0) rows.push({ label: `Line ${idx + 1}`, detail: 'Quantity must be greater than 0', group: 'Line Items', keys: ['lines'] })
    return rows
  })

  const unmatchedSummaryItems = [
    !vendorResolved && { label: 'Vendor', detail: 'Not confirmed yet', group: 'Vendor', keys: ['vendor'] },
    missingSubsidiary && { label: 'Subsidiary', detail: 'Required — not selected', group: 'Header Fields', keys: ['subsidiary'] },
    missingLocation && { label: 'Location', detail: 'Required — not selected', group: 'Header Fields', keys: ['location'] },
    missingShippingTerms && { label: 'Shipping Terms', detail: 'Required — not selected', group: 'Header Fields', keys: ['shippingTerms'] },
    missingTranDate && { label: tranDateField?.display_name || 'Transaction Date', detail: 'Required — still empty', group: 'Header Fields', keys: tranDateField ? [`field-${tranDateField.id}`] : [] },
    ...missingRequiredFields.map(f => ({ label: f.display_name, detail: 'Required — still empty', group: 'Header Fields', keys: [`field-${f.id}`] })),
    ...extraHeaderIssues.map(e => ({
      label: headerFields.find(hf => hf.ns_field === e.field_key)?.display_name || e.field_key || 'Field',
      detail: e.message,
      group: 'Header Fields',
      keys: [resolveFieldKey(e.field_key)].filter(Boolean),
    })),
    validLineCount === 0 && { label: 'Line items', detail: 'No line items added', group: 'Line Items', keys: ['lines'] },
    ...lineIssueRows,
  ].filter(Boolean)

  const [showChecklist, setShowChecklist] = useState(false)
  const [isPdfViewerVisible, setIsPdfViewerVisible] = useState(true)
  const [showAiLog, setShowAiLog] = useState(false)

  // Left-panel tabs: Fields / Activity / AI Insights (no Exports — not applicable to NS)
  const [leftPanelTab, setLeftPanelTab] = useState('fields')
  const [fieldSearchQuery, setFieldSearchQuery] = useState('')
  const [fieldsExtractionFilter, setFieldsExtractionFilter] = useState('all') // 'all', 'extracted', 'not_extracted'
  const [fieldsViewFilter, setFieldsViewFilter] = useState('all') // 'all', 'mandatory' or 'custom'
  const { tabRefs: fieldsViewTabRefs, indicatorStyle: fieldsViewIndicatorStyle } = useSegmentedTabIndicator(fieldsViewFilter, [leftPanelTab])
  const [activityLogs, setActivityLogs] = useState([])
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activityLoaded, setActivityLoaded] = useState(false)

  const fetchActivityLogs = useCallback(async () => {
    if (!request?.id) return
    try {
      setLoadingActivity(true)
      const res = await requestsApi.getAuditLogs(request.id, { limit: 20 })
      setActivityLogs(formatAuditLogsForActivity(res.data.audit_logs || []))
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setActivityLogs([])
    } finally {
      setLoadingActivity(false)
      setActivityLoaded(true)
    }
  }, [request?.id])

  useEffect(() => {
    if (leftPanelTab === 'activity' && !activityLoaded) {
      fetchActivityLogs()
    }
  }, [leftPanelTab, activityLoaded, fetchActivityLogs])

  // Draggable split between Fields panel (left) and document viewer (right),
  // matching the BC/normal extraction page's fields-left/document-right layout.
  // Value = % width of the left (Fields) panel.
  const [leftPanelPct, setLeftPanelPct] = useState(50)
  const [isDraggingDivider, setIsDraggingDivider] = useState(false)
  const mainContentRef = useRef(null)

  const handleDividerMouseDown = (e) => {
    e.preventDefault()
    setIsDraggingDivider(true)

    const onMouseMove = (moveEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      let pct = ((moveEvent.clientX - rect.left) / rect.width) * 100
      pct = Math.min(80, Math.max(20, pct))
      setLeftPanelPct(pct)
    }
    const onMouseUp = () => {
      setIsDraggingDivider(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // Action bar state
  // Collapse the header's chip row / status row to reclaim vertical space —
  // same affordance as the normal extraction page's header.
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [sendingForReview, setSendingForReview] = useState(false)

  // Field to highlight in the PDF viewer (bounding_box + page_number), set by
  // clicking a field row — same "click to jump to source" behaviour as the
  // normal request page.
  const [highlightedField, setHighlightedField] = useState(null)

  // Alternatives popup — per-field, keyed by field id so only one is open at a time.
  const [openAlternativesFieldId, setOpenAlternativesFieldId] = useState(null)
  const [alternatives, setAlternatives] = useState([])
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)
  const alternativesPopupRef = useRef(null)

  // Audit history popup — per-field.
  const [openAuditFieldId, setOpenAuditFieldId] = useState(null)
  const [fieldAuditLogs, setFieldAuditLogs] = useState([])
  const [loadingFieldAudit, setLoadingFieldAudit] = useState(false)
  const auditPopupRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alternativesPopupRef.current && !alternativesPopupRef.current.contains(event.target)) {
        setOpenAlternativesFieldId(null)
      }
      if (auditPopupRef.current && !auditPopupRef.current.contains(event.target)) {
        setOpenAuditFieldId(null)
      }
    }
    if (openAlternativesFieldId != null || openAuditFieldId != null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openAlternativesFieldId, openAuditFieldId])

  const handleViewFieldInDocument = (field) => {
    const hasValue = !!(field?.value ?? '').toString().trim()
    if (!hasValue) {
      setHighlightedField(null)
      return
    }
    setHighlightedField({
      bounding_box: field.bounding_box,
      page_number: field.page_number,
      field_name: field.field_name,
      extracted_value: field.value,
    })
  }

  const handleToggleAlternatives = async (field) => {
    if (openAlternativesFieldId === field.id) {
      setOpenAlternativesFieldId(null)
      return
    }
    setOpenAuditFieldId(null)
    setOpenAlternativesFieldId(field.id)
    setLoadingAlternatives(true)
    try {
      const res = await requestsApi.getFieldAlternatives(request.id, field.field_name)
      setAlternatives(res.data.alternatives || [])
    } catch (err) {
      console.error('Error loading field alternatives:', err)
      setAlternatives([])
    } finally {
      setLoadingAlternatives(false)
    }
  }

  const handleSelectAlternative = (field, alt) => {
    const newValue = alt.field_value || alt.extracted_value || ''
    setFieldValues(prev => ({ ...prev, [field.id]: newValue }))
    setOpenAlternativesFieldId(null)
    if (alt.bounding_box || alt.page_number) {
      setHighlightedField({
        bounding_box: alt.bounding_box,
        page_number: alt.page_number,
        field_name: field.field_name,
        extracted_value: newValue,
      })
    }
  }

  const handleToggleFieldAudit = async (field) => {
    if (openAuditFieldId === field.id) {
      setOpenAuditFieldId(null)
      return
    }
    setOpenAlternativesFieldId(null)
    setOpenAuditFieldId(field.id)
    setLoadingFieldAudit(true)
    try {
      const res = await requestsApi.getFieldAuditLogs(request.id, field.id, { limit: 20 })
      setFieldAuditLogs(res.data.audit_logs || [])
    } catch (err) {
      console.error('Error loading field audit log:', err)
      setFieldAuditLogs([])
    } finally {
      setLoadingFieldAudit(false)
    }
  }

  const handleCancel = useCallback(async () => {
    if (!cancelReason.trim()) return
    setCancelling(true)
    try {
      await requestsApi.cancel(request.id, cancelReason)
      setShowCancelModal(false)
      navigate(-1)
    } catch {
      toast.error('Failed to cancel request')
    } finally {
      setCancelling(false)
    }
  }, [cancelReason, request.id, navigate, toast])

  const handleSendForReview = useCallback(async () => {
    // Run the same NS validation that handlePost runs before allowing submission.
    setSendingForReview(true)
    try {
      // Same shared projection handlePost and the local validator use.
      const hf = nsHeaderPayload

      const cleanLines = lineItems
        .filter(l => l.nsItemId && (parseFloat(l.quantity) || 0) > 0)
        .map(l => ({
          item: l.nsItemId,
          description: l.description || '',
          quantity: parseFloat(l.quantity),
          ...(l.directUnitCost !== '' && l.directUnitCost != null ? { rate: parseFloat(l.directUnitCost) || 0 } : {}),
        }))

      // Validate against NetSuite before sending for review
      const vRes = await nsApi.validate({
        request_id: request.id,
        org_id: request.organization_id,
        payload: hf,
        line_items: cleanLines,
      })
      if (!vRes.data?.valid) {
        setServerErrors(vRes.data?.errors || [])
        toast.error('Fix validation errors before sending for review')
        return
      }

      // Persist review state so the reviewer sees up-to-date values
      await persistReviewState()

      // Send for review
      await requestsApi.sendForReview(request.id)
      toast.success('Sent for review — the request is now in the Approvals queue.')
      navigate('/approvals')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send for review')
    } finally {
      setSendingForReview(false)
    }
  }, [nsHeaderPayload, lineItems, request.id, request.organization_id, navigate, toast])

  // Average confidence across all header fields that have a confidence value
  const avgConfidence = useMemo(() => {
    const withConf = headerFields.filter(f => f.confidence != null && f.confidence > 0)
    if (!withConf.length) return null
    return Math.min(100, Math.max(0, Math.round(
      (withConf.reduce((s, f) => s + (f.confidence || 0), 0) / withConf.length) * 100
    )))
  }, [headerFields])

  const mandateReview = !!request?.template?.mandate_review
  const reviewDone = !mandateReview || ['reviewing', 'pending_approval', 'in_review', 'completed', 'published'].includes(request?.status)
  const canPost = unmatchedSummaryItems.length === 0 && !posting && !postResult?.success && reviewDone

  const pdfDoc = (request?.documents || []).find(d => d.content_type === 'application/pdf' || d.filename?.endsWith('.pdf'))
  const pdfDocId = pdfDoc?.id
  const orgId = request?.organization_id || ''

  // ── Render ──
  return (
    <>
    {/* AI Processing overlay */}
    {showProcessingModal && (
      processingMinimized ? (
        <button onClick={() => setProcessingMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Processing...</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{processingProgress.percent}% — click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 ml-1" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={() => setProcessingMinimized(true)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-10 animate-pulse" />
            <div className="relative flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                  <Bot className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">AI is Extracting Details</h3>
              <div className="w-full mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{processingProgress.message || 'Starting...'}</span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{processingProgress.percent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(processingProgress.percent, 2)}%` }} />
                </div>
              </div>
              <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto text-left">
                {processingLog.length === 0 ? (
                  <div className="p-3 flex items-center gap-2 text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /><span>Waiting…</span></div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {processingLog.map((step, idx) => (
                      <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                        <span className="text-[10px] font-mono text-gray-400 whitespace-nowrap mt-0.5">{step.elapsed_seconds != null ? `${step.elapsed_seconds}s` : step.timestamp}</span>
                        <span className={`text-xs ${step.message?.includes('✅') ? 'text-green-600' : step.message?.includes('❌') ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{step.message}</span>
                      </div>
                    ))}
                    <div ref={el => el?.scrollIntoView({ behavior: 'smooth', block: 'end' })} />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Click anywhere outside to minimize</p>
            </div>
          </div>
        </div>
      )
    )}

    <div className="-m-6 flex flex-col bg-gray-100 dark:bg-gray-950 overflow-hidden animate-page-in" style={{ height: 'calc(100vh - 4rem)' }}>

      <UnassignedOpCoBanner request={request} onMoved={onOpCoMoved} />
      {/* Top header bar — same gradient/dot-pattern + icon-badge + title design as the BC/normal extraction page */}
      <div className="relative bg-gradient-to-r from-navy-100/90 via-navy-50/70 to-navy-100/80 dark:from-gray-900/80 dark:via-gray-800/60 dark:to-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 animate-header-in">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, var(--tw-color-navy-900, #0f2942) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-navy-600 dark:bg-navy-500"
        />
        <div
          className={`relative z-10 pl-6 pr-5 flex items-start justify-between gap-4 flex-wrap transition-[padding] duration-300 ease-out ${
            isHeaderCollapsed ? 'py-2.5' : 'pt-4 pb-3'
          }`}
        >
          {/* LEFT: icon badge + title + template/file chips — collapsible, same affordance as the normal extraction page */}
          <div className={`relative flex min-w-0 flex-1 basis-[280px] ${isHeaderCollapsed ? 'items-center gap-3' : 'items-start gap-3.5'}`}>
            <div
              className={`hidden sm:flex flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-600 to-navy-800 text-white shadow-md ring-1 ring-navy-900/10 transition-all ${
                isHeaderCollapsed ? 'h-9 w-9' : 'h-11 w-11'
              }`}
              title="NetSuite document request"
            >
              <FileText className={isHeaderCollapsed ? 'h-4 w-4' : 'h-5 w-5'} />
            </div>
            <div className="min-w-0 flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <h1
                  className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-snug truncate"
                  title="Request title"
                >
                  {pdfDoc?.filename || request?.name || 'Untitled Request'}
                </h1>
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0"
                  title="NetSuite record type this request will post as"
                >
                  NS PO
                </span>
                <button
                  type="button"
                  onClick={() => setIsHeaderCollapsed(prev => !prev)}
                  title={isHeaderCollapsed ? 'Expand details' : 'Collapse details'}
                  className="flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 dark:hover:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isHeaderCollapsed ? '-rotate-90' : ''}`} />
                </button>
                <OpCoBadge request={request} />
                <MoveOpCoButton request={request} onMoved={onOpCoMoved} />
              </div>
              {!isHeaderCollapsed && (request?.template_name || request?.template?.name || pdfDoc?.filename) && (
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {(request?.template_name || request?.template?.name) && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                      title="Extraction template used for this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileSpreadsheet className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {request?.template_name || request?.template?.name}
                      </span>
                    </span>
                  )}
                  {pdfDoc?.filename && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                      title="Source document attached to this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileText className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {pdfDoc.filename}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* RIGHT: AI confidence / status (top row) + action buttons (below) */}
          <div className={`flex flex-shrink-0 gap-2 ${isHeaderCollapsed ? 'flex-row items-center' : 'flex-col items-end'}`}>
            {!isHeaderCollapsed && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 shrink min-w-0 whitespace-nowrap">
                {avgConfidence != null && (
                  <span className={`group relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 cursor-default ${
                    avgConfidence >= 90
                      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                      : avgConfidence >= 70
                      ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400'
                      : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    AI Confidence: <strong>{avgConfidence}%</strong>

                    {/* Confidence legend — shown on hover, same as the normal extraction page */}
                    <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                      <div className="flex items-center gap-2.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&gt;90%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">70-89%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&lt;70%</span>
                        </div>
                      </div>
                    </div>
                  </span>
                )}
                {lookupsLoading && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 flex-shrink-0">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-xs whitespace-nowrap">Loading NS data…</span>
                  </span>
                )}
                {/* Saved timestamp — informational, stays above the action buttons */}
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 flex-shrink-0">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="text-xs whitespace-nowrap">
                    Saved {request?.updated_at ? formatTimeAgo(request.updated_at) : 'N/A'}
                  </span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-2.5 flex-wrap justify-end">
              {request?.can_cancel !== false && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold text-red-600 dark:text-red-300 bg-white dark:bg-gray-900 border border-red-300 dark:border-red-400/40 hover:bg-red-50 hover:border-red-400 dark:hover:bg-red-400/10 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.96]"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div ref={mainContentRef} className={`flex flex-1 overflow-hidden ${isDraggingDivider ? 'select-none cursor-col-resize' : ''}`}>

      {/* LEFT: Fields — fields on the left, document on the right, matching the BC/normal extraction page */}
      <section
        style={{ width: isPdfViewerVisible ? `${leftPanelPct}%` : '100%' }}
        className={`min-w-[320px] flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 overflow-hidden ${isDraggingDivider ? '' : 'transition-all duration-150'}`}
      >
        <div className="px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/60 dark:to-gray-900 pt-3.5 pb-3.5">
          <RequestPanelTabs
            tabs={[
              { key: 'fields', label: 'Fields', icon: ClipboardList },
              { key: 'activity', label: 'Activity', icon: Activity },
              { key: 'ai_ops', label: 'AI Insights', icon: Sparkles },
            ]}
            activeTab={leftPanelTab}
            onTabChange={setLeftPanelTab}
            isDocViewerVisible={isPdfViewerVisible}
            onToggleDocViewer={() => { setIsPdfViewerVisible(v => !v); setLeftPanelPct(50) }}
          />
          {leftPanelTab === 'fields' && (
            <FieldsPanelHeader
              fieldCount={headerFields.length}
              searchQuery={fieldSearchQuery}
              onSearchChange={setFieldSearchQuery}
              extractionFilter={fieldsExtractionFilter}
              onExtractionFilterChange={setFieldsExtractionFilter}
              extractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type)).length
              }
              notExtractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => !f.source_type || f.source_type === 'pending').length
              }
              viewFilter={fieldsViewFilter}
              onViewFilterChange={setFieldsViewFilter}
              viewIndicatorStyle={fieldsViewIndicatorStyle}
              viewTabRefs={fieldsViewTabRefs}
            />
          )}
        </div>

        {leftPanelTab === 'activity' && (
          <ActivityLogPanel logs={activityLogs} loading={loadingActivity} onRefresh={fetchActivityLogs} />
        )}

        {leftPanelTab === 'ai_ops' && (
          <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
            <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mb-3">AI Insights</h3>
            <CostBreakdown requestId={request.id} embedded />
          </div>
        )}

        {leftPanelTab === 'fields' && lookupsError && (
          <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex-shrink-0">
            {lookupsError}
          </div>
        )}

        {leftPanelTab === 'fields' && (
        <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-3 space-y-5 animate-panel-in">

          {/* ── AI Operations log ── */}
          {processingLog.length > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setShowAiLog(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-navy-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">AI Operations</span>
                  <span className="text-xs text-gray-400">({processingLog.length} steps)</span>
                  {!showAiLog && (
                    <span className="text-[10px] text-gray-400">
                      {request?.last_job?.status === 'completed'
                        ? `✅ Completed in ${processingLog[processingLog.length - 1]?.elapsed_seconds || '?'}s`
                        : request?.last_job?.status === 'failed'
                        ? `❌ Failed`
                        : ''}
                    </span>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${showAiLog ? 'rotate-180' : ''}`} />
              </button>
              {showAiLog && (
                <div className="bg-gray-900 dark:bg-black max-h-64 overflow-y-auto font-mono text-[11px]">
                  {processingLog.map((step, idx) => (
                    <div key={idx} className="px-3 py-1 border-b border-gray-800 flex gap-2 hover:bg-gray-800">
                      <span className="text-gray-500 whitespace-nowrap min-w-[40px]">
                        {step.elapsed_seconds != null ? `${step.elapsed_seconds}s` : step.timestamp}
                      </span>
                      <span className={
                        step.message?.includes('✅') ? 'text-green-400' :
                        step.message?.includes('❌') ? 'text-red-400' :
                        step.message?.includes('⚠️') ? 'text-yellow-400' :
                        step.message?.includes('⏳') ? 'text-blue-400' :
                        'text-gray-300'
                      }>{step.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Vendor field ── */}
          {(() => {
            const vendorFields = headerFields.filter(f => VENDOR_FIELD_KEYS.has(f.ns_field))
            const primaryVendorField = vendorFields[0]
            if (!primaryVendorField) return null
            const vConfidence = primaryVendorField.confidence || 0
            const vConfidencePercent = Math.round(vConfidence * 100)
            const vIsHighConf = vConfidence >= 0.9
            const vIsMedConf = vConfidence >= 0.7 && vConfidence < 0.9
            const vHasValue = !!(primaryVendorField.value ?? '').toString().trim()
            return (
              <div
                ref={el => { fieldRowRefs.current['vendor'] = el }}
                className={`space-y-1 p-1 -m-1 transition-shadow ${jumpHighlightClass('vendor')}`}
              >
                <label
                  onClick={() => handleViewFieldInDocument(primaryVendorField)}
                  className={`flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 ${vHasValue ? 'cursor-pointer w-fit' : ''}`}
                >
                  {showColorCoding && vHasValue && vConfidence > 0 && (
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${vIsHighConf ? 'bg-green-500' : vIsMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                  )}
                  Vendor <span className="text-red-500">*</span>
                  {showConfidenceScore && vHasValue && vConfidence > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      vIsHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : vIsMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>{vConfidencePercent}%</span>
                  )}
                </label>
                <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                  <VendorField
                    extractedValue={primaryVendorField.value}
                    vendors={lookups.vendors || []}
                    onVendorResolved={handleVendorResolved}
                    onVendorCleared={handleVendorCleared}
                    requestId={request.id}
                    orgId={orgId}
                    templateId={request.template_id}
                    // Scope the vendor search by subsidiary ONLY while the
                    // subsidiary did not come from a resolved vendor. Once a vendor
                    // resolves, the subsidiary is locked to that vendor's own, and
                    // feeding it back into the search would be circular.
                    subsidiaryId={vendorSubsidiaryId ? null : selectedSubsidiary?.id}
                  />
                </EditGate>
                {vendorResolved && (
                  <div className="grid grid-cols-2 gap-2 mt-2 animate-panel-in">
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">NS Internal ID</p>
                      <p className="text-xs font-mono font-semibold text-gray-700 dark:text-gray-300 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">{vendorNsId}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">Vendor Name</p>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 truncate">{vendorNsName}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

          {/* ── NS-specific: Subsidiary (required) ── */}
          <div
            ref={el => { fieldRowRefs.current['subsidiary'] = el }}
            className={`space-y-1 p-1 -m-1 transition-shadow ${jumpHighlightClass('subsidiary')}`}
          >
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              Subsidiary <span className="text-red-500">*</span>
            </label>
            <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
              <SearchableDropdown
                value={selectedSubsidiary?.id}
                options={subsidiaryOptions}
                onChange={opt => setSelectedSubsidiary(opt)}
                placeholder="Select subsidiary…"
                loading={lookupsLoading}
                required
              />
            </EditGate>
            {vendorSubsidiaryId && (
              <p className="text-[10px] text-gray-400">Locked to the vendor's subsidiary</p>
            )}
          </div>

          {/* ── NS-specific: Location (required, filtered by subsidiary) ── */}
          <div
            ref={el => { fieldRowRefs.current['location'] = el }}
            className={`space-y-1 p-1 -m-1 transition-shadow ${jumpHighlightClass('location')}`}
          >
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              Location <span className="text-red-500">*</span>
              {selectedSubsidiary && <span className="text-[10px] text-gray-400 font-normal">filtered by {selectedSubsidiary.name}</span>}
            </label>
            <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
              <SearchableDropdown
                value={selectedLocation?.id}
                options={filteredLocations}
                onChange={opt => setSelectedLocation(opt)}
                placeholder="Select location…"
                loading={lookupsLoading}
                required
              />
            </EditGate>
          </div>

          {/* ── NS-specific: Currency (optional) ── */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">Currency</label>
            <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
              <SearchableDropdown
                value={selectedCurrency?.id}
                options={currencies}
                onChange={opt => setSelectedCurrency(opt)}
                placeholder="Select currency…"
                loading={lookupsLoading}
                extractedText={extractedCurrencyText}
              />
            </EditGate>
          </div>

          {/* ── NS-specific: Shipping Terms (custbody16) ── */}
          <div
            ref={el => { fieldRowRefs.current['shippingTerms'] = el }}
            className={`space-y-1 p-1 -m-1 transition-shadow ${jumpHighlightClass('shippingTerms')}`}
          >
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              Shipping Terms <span className="text-red-500">*</span>
              <span className="text-[10px] text-gray-400 font-normal font-mono">(custbody16)</span>
            </label>
            <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
              <SearchableDropdown
                value={selectedShippingTerms?.id}
                options={shippingTermsList}
                onChange={opt => setSelectedShippingTerms(opt)}
                placeholder="Select shipping terms…"
                loading={lookupsLoading}
                extractedText={extractedShippingTermsText}
                required
              />
            </EditGate>
          </div>

          {/* ── Regular header fields (skip vendor + NS auto fields) ── */}
          {headerFields.length === 0 && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold">No extracted request fields found for this record.</p>
              <p className="mt-1">This can happen when extraction failed or the PO was posted manually. Add line items and required values, then post again to save data into this request.</p>
              {request?.ns_po_number && (
                <p className="mt-1.5">
                  Linked NetSuite PO: {request.ns_url ? (
                    <a href={request.ns_url} target="_blank" rel="noopener noreferrer" className="font-mono font-semibold underline hover:text-amber-900 dark:hover:text-amber-100">{request.ns_po_number}</a>
                  ) : (
                    <span className="font-mono font-semibold">{request.ns_po_number}</span>
                  )}
                </p>
              )}
            </div>
          )}

          {(() => {
            let visibleFields = headerFields
            if (fieldsViewFilter === 'mandatory') {
              visibleFields = visibleFields.filter(f => f.is_required)
            } else if (fieldsViewFilter === 'custom') {
              visibleFields = visibleFields.filter(f => f.source_type === 'user_input')
            }
            if (fieldsExtractionFilter === 'extracted') {
              visibleFields = visibleFields.filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type))
            } else if (fieldsExtractionFilter === 'not_extracted') {
              visibleFields = visibleFields.filter(f => !f.source_type || f.source_type === 'pending')
            }
            if (fieldSearchQuery.trim()) {
              const q = fieldSearchQuery.toLowerCase()
              visibleFields = visibleFields.filter(f => (f.display_label || f.field_name || '').toLowerCase().includes(q))
            }
            return visibleFields
          })().map(field => {
            if (VENDOR_FIELD_KEYS.has(field.ns_field)) return null
            if (NS_AUTO_FIELDS.has(field.ns_field)) return null

            // NS custom fields (custbody_*): use human-readable label from ns_field_rules
            const isCustomField = (field.ns_field || '').startsWith('custbody_')

            const serverErr = validationErrors.find(e => e.field_key === field.ns_field || e.field_key === field.field_name || e.field === field.ns_field)
            const inputType = getInputType(field)
            const val = fieldValues[field.id] ?? ''
            const isEmpty = val === '' || val === null
            // NetSuite always requires a transaction date, regardless of whether this
            // template's config happens to mark the field required — treat it the same
            // as a template-required field everywhere below (star, border, gating).
            const isRequired = field.is_required || (field.ns_field || '').toLowerCase() === 'trandate'

            const confidence = field.confidence || 0
            const confidencePercent = Math.round(confidence * 100)
            const isLowConf = confidence > 0 && confidence < 0.7
            const isMedConf = confidence >= 0.7 && confidence < 0.9
            const isHighConf = confidence >= 0.9

            const hasError = !!serverErr || (isRequired && isEmpty)
            const getFieldBorderColor = () => {
              if (serverErr) return 'border-2 border-red-400 dark:border-red-600 focus:ring-red-400 hover:ring-2 hover:ring-red-300 dark:hover:ring-red-700'
              if (isRequired && isEmpty) return 'border-2 border-amber-400 dark:border-amber-500 focus:ring-amber-400 hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-700'
              if (!showColorCoding || isEmpty) return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
              if (isLowConf) return 'border-2 border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 focus:ring-red-400 hover:ring-2 hover:ring-red-300 dark:hover:ring-red-700'
              if (isMedConf) return 'border-2 border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20 focus:ring-orange-400 hover:ring-2 hover:ring-orange-300 dark:hover:ring-orange-700'
              return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
            }
            const tooltipText = serverErr
              ? serverErr.message
              : (isRequired && isEmpty)
              ? 'This field is required before the PO can be posted.'
              : (!isEmpty && isLowConf)
              ? `Low extraction confidence (${confidencePercent}%) — please verify this value against the source document.`
              : (!isEmpty && isMedConf)
              ? `Medium confidence (${confidencePercent}%) — double-check this value.`
              : null

            const alternativeCount = field.alternative_count || 0
            const hasAlternatives = alternativeCount >= 1
            const isAlternativesOpen = openAlternativesFieldId === field.id
            const isAuditOpen = openAuditFieldId === field.id

            return (
              <FieldTooltip key={field.id} text={tooltipText}>
              <div
                ref={el => { fieldRowRefs.current[`field-${field.id}`] = el }}
                onClick={() => handleViewFieldInDocument(field)}
                className={`relative space-y-1 p-1 -m-1 cursor-pointer transition-shadow ${hasError ? 'cursor-help' : ''} ${jumpHighlightClass(`field-${field.id}`)}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 min-w-0">
                    {showColorCoding && !isEmpty && confidence > 0 && (
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHighConf ? 'bg-green-500' : isMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                    )}
                    <span className="truncate">{field.display_name}</span>
                    {isRequired && <span className="text-red-500">*</span>}
                    {field.ns_field && (
                      <span className={`font-mono text-[10px] px-1 rounded ${isCustomField ? 'text-navy-600 bg-navy-50 dark:bg-navy-900/20 dark:text-navy-300' : 'text-gray-400 bg-gray-100 dark:bg-gray-800'}`}>{field.ns_field}</span>
                    )}
                    {showConfidenceScore && !isEmpty && confidence > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${isHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : isMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{confidencePercent}%</span>
                    )}
                    {showAiBadge && !isEmpty && field.source_type === 'llm_fallback' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">AI</span>
                    )}
                  </label>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleToggleFieldAudit(field) }}
                      className={`flex-shrink-0 p-1 rounded-md transition-all ${isAuditOpen ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'}`}
                      title="View edit history"
                    >
                      <History className="h-3.5 w-3.5" strokeWidth={isAuditOpen ? 2.75 : 2} />
                    </button>
                    {hasAlternatives && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleAlternatives(field) }}
                        className={`flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-all border ${isAlternativesOpen ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                        title={`${alternativeCount} alternative value(s) from different documents`}
                      >
                        <Layers className="h-3 w-3" />
                        <span>{alternativeCount + 1}</span>
                      </button>
                    )}
                  </div>
                </div>

                <input
                  type={inputType}
                  value={val}
                  readOnly
                  onClick={e => e.stopPropagation()}
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-1 transition-colors duration-200 bg-gray-50 dark:bg-gray-800/60 cursor-text ${getFieldBorderColor()}`}
                />

                {/* Alternatives popup */}
                {isAlternativesOpen && (
                  <div
                    ref={alternativesPopupRef}
                    onClick={e => e.stopPropagation()}
                    className="absolute z-50 top-full right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    style={{ width: '320px' }}
                  >
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Select value <span className="text-gray-400">({alternatives.length + 1} found)</span>
                      </span>
                      <button onClick={() => setOpenAlternativesFieldId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingAlternatives ? (
                      <div className="p-6 text-center">
                        <RefreshCw className="h-4 w-4 animate-spin mx-auto text-blue-500" />
                        <span className="text-xs text-gray-500 mt-2 block">Loading...</span>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {[{ id: field.id, field_value: field.value, confidence: field.confidence, source_document_filename: field.source_document_filename, page_number: field.page_number, isActive: true }, ...alternatives.map(a => ({ ...a, isActive: false }))].map(item => (
                          <div
                            key={item.id}
                            className={`px-3 py-2.5 transition-all cursor-pointer flex items-center gap-3 ${item.isActive ? 'bg-green-50 dark:bg-green-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            onClick={() => !item.isActive && handleSelectAlternative(field, item)}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${item.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-blue-500'}`}>
                              {item.isActive && <Check className="h-2.5 w-2.5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${item.isActive ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                {item.field_value || item.extracted_value || '(empty)'}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">
                                {item.source_document_filename || 'Unknown source'}
                                {item.page_number && <span className="ml-1">• p.{item.page_number}</span>}
                              </p>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${(item.confidence || 0) >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : (item.confidence || 0) >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                              {Math.round((item.confidence || 0) * 100)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Audit history popup */}
                {isAuditOpen && (
                  <AnchoredPopover
                    ref={auditPopupRef}
                    onClick={e => e.stopPropagation()}
                    anchorClassName="top-full right-0"
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
                    style={{ width: '340px' }}
                  >
                    <div className="px-3.5 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 flex items-center justify-between">
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" /> Edit History
                      </span>
                      <button onClick={() => setOpenAuditFieldId(null)} className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-md transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingFieldAudit ? (
                      <div className="p-6 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto text-navy-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">Loading history...</span>
                      </div>
                    ) : fieldAuditLogs.length === 0 ? (
                      <div className="p-6 text-center">
                        <History className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">No edit history found</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {fieldAuditLogs.map((log, idx) => {
                          let oldValue = null, newValue = null
                          try {
                            const changes = typeof log.audit_json === 'string' ? JSON.parse(log.audit_json) : log.audit_json
                            Object.keys(changes || {}).forEach(key => {
                              if (changes[key].old !== undefined || changes[key].new !== undefined) {
                                oldValue = changes[key].old
                                newValue = changes[key].new
                              }
                            })
                          } catch { /* ignore malformed audit_json */ }
                          const actorName = log.created_by_name || log.created_by || 'System'
                          return (
                            <div key={log.id || idx} className="px-3.5 py-2.5">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{actorName}</span>
                                <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                  {log.created_at ? formatDateTime(log.created_at) : ''}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {oldValue !== null && oldValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 w-8 flex-shrink-0 pt-0.5">From</span>
                                    <span className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(oldValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                                {newValue !== null && newValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 pt-0.5">To</span>
                                    <span className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(newValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </AnchoredPopover>
                )}

                {serverErr && <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3 flex-shrink-0" /> {serverErr.message}</p>}
                {!serverErr && isRequired && isEmpty && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3 flex-shrink-0" /> Required</p>
                )}
                {!serverErr && !isEmpty && isLowConf && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3 flex-shrink-0" /> Low confidence — please verify</p>
                )}
              </div>
              </FieldTooltip>
            )
          })}
        </div>

        {/* ── Line Items ── */}
        <div
          ref={el => { fieldRowRefs.current['lines'] = el }}
          className={`px-4 pt-3 pb-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 transition-shadow ${jumpHighlightClass('lines')}`}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Purchase Order Lines</p>
            {lineItems.filter(l => l.lineObjectNumber || l.description).length > 0 && (
              <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                {lineItems.filter(l => l.lineObjectNumber || l.description).length} line{lineItems.filter(l => l.lineObjectNumber || l.description).length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
            <LineItemsTable lines={lineItems} items={nsItems} onChange={next => setLineItems(next)} subsidiaryId={selectedSubsidiary?.id} needsReview={lineItemTableField?.needs_review} reviewReason={lineItemTableField?.review_reason} isEditMode={isEditable} />
          </EditGate>
        </div>

        {/* ── Validate & Post ── */}
        <div className="px-4 pt-3 pb-5 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 space-y-2.5">

          {/* Validation — evaluated locally, so there is no spinner and no
              intermediate loading state: the verdict updates within the frame.
              Hidden once posted — an already-completed request has nothing
              left to fix. */}
          {!postResult?.success && rulesDegraded && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 p-3 flex items-start gap-2 animate-panel-in">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                  Validation rules couldn’t be loaded
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Using baseline checks. Template-specific required fields will still be
                  verified when you post.
                </p>
                <button
                  type="button"
                  onClick={reloadLookups}
                  className="mt-1.5 text-xs font-medium text-amber-800 dark:text-amber-200 underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Unified pre-post checklist — a single collapsible card instead of a
              standalone vendor box + a raw dump of every local-validator message,
              so the panel reads as one status at a glance and expands into
              grouped, click-to-jump detail only when the user wants it. */}
          {!postResult?.success && (() => {
            const ready = unmatchedSummaryItems.length === 0
            const groupOrder = ['Vendor', 'Header Fields', 'Line Items']
            const grouped = groupOrder
              .map(group => ({ group, items: unmatchedSummaryItems.filter(i => i.group === group) }))
              .filter(g => g.items.length > 0)

            return (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  type="button"
                  onClick={() => !ready && setShowChecklist(o => !o)}
                  className={`w-full flex items-center gap-2 px-3.5 py-2.5 transition-colors ${
                    ready
                      ? 'bg-green-50 dark:bg-green-900/10 cursor-default'
                      : 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                  }`}
                >
                  {ready
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />}
                  <p className={`text-sm font-semibold flex-1 text-left ${ready ? 'text-green-700 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
                    {ready
                      ? 'Ready to post as Purchase Order'
                      : `${unmatchedSummaryItems.length} thing${unmatchedSummaryItems.length !== 1 ? 's' : ''} to fix before posting`}
                  </p>
                  {!ready && (
                    <ChevronDown className={`h-4 w-4 text-amber-400 transition-transform flex-shrink-0 ${showChecklist ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {!ready && showChecklist && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800 animate-panel-in">
                    {grouped.map(({ group, items }) => (
                      <div key={group} className="px-3.5 py-2.5">
                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                          {group} ({items.length})
                        </p>
                        <div className="space-y-0.5 -mx-1.5">
                          {items.map((item, i) => {
                            const clickable = item.keys && item.keys.length > 0
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => clickable && jumpToFieldKeys(item.keys)}
                                disabled={!clickable}
                                title={clickable ? `Jump to ${item.label}` : item.label}
                                className={`w-full flex items-start justify-between gap-2 px-1.5 py-1 rounded text-left transition-colors ${
                                  clickable ? 'hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer' : 'cursor-default'
                                }`}
                              >
                                <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 min-w-0 flex-shrink-0 max-w-[40%]">
                                  <span className="truncate">{item.label}</span>
                                  {clickable && <ChevronRight className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                                </span>
                                <span className="text-xs font-medium text-amber-700 dark:text-amber-300 text-right">{item.detail}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* Item↔subsidiary provenance — advisory only, never gates posting.
              NetSuite's itemsubsidiarymap cannot be held in the browser, so this
              reports lines whose item was resolved under a different subsidiary;
              they re-resolve automatically and the server checks them before post. */}
          {!postResult?.success && unverifiedForSubsidiaryLines.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                {unverifiedForSubsidiaryLines.length} line
                {unverifiedForSubsidiaryLines.length !== 1 ? 's' : ''} not yet verified for
                “{selectedSubsidiary?.name}” — this is confirmed automatically when you post.
              </span>
            </div>
          )}

          {/* NS pending approval banner */}
          {pendingApproval && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 p-3 flex items-start gap-2 animate-panel-in">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Awaiting supervisor approval in NetSuite</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">The PO was created but requires supervisor approval before it is finalised in NetSuite.</p>
              </div>
            </div>
          )}

          {/* Post result */}
          {postResult?.success && (
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-3 animate-fade-in">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" /><p className="text-sm font-semibold text-green-700 dark:text-green-300">Posted successfully to NetSuite</p></div>
              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
                PO: <span className="font-mono font-bold">{postResult.po_number}</span>
                {postResult.ns_url && (
                  <a href={postResult.ns_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-green-800 dark:hover:text-green-200">
                    Open in NetSuite <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </p>
            </div>
          )}
          {postResult && !postResult.success && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 p-3 space-y-2 animate-fade-in">
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" /><p className="text-xs font-semibold text-red-700 dark:text-red-300">Failed ({postResult.attempt}/{MAX_ATTEMPTS})</p></div>
              <p className="text-xs text-red-600 dark:text-red-400">{postResult.error}</p>
              {postAttempts >= MAX_ATTEMPTS
                ? <p className="text-xs text-gray-500 italic">Max attempts reached.</p>
                : <button onClick={handleRetry} disabled={posting} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-100 disabled:opacity-50 transition-all duration-150 active:scale-[0.97]">
                    {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry
                  </button>
              }
            </div>
          )}

          {/* Post + Send for Review buttons */}
          <div className="flex flex-col items-center pt-2 gap-2">

            {mandateReview && !reviewDone ? (
              /* ── Mandate review ON and not yet reviewed: Send for Review is the primary action ── */
              <div className="flex flex-col w-full max-w-md gap-2">
                <button
                  onClick={handleSendForReview}
                  disabled={sendingForReview || posting || !vendorResolved}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-navy-500 hover:bg-navy-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow"
                >
                  {sendingForReview
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Validating &amp; Sending…</>
                    : <><SendHorizonal className="h-4 w-4" /> Send for Review</>}
                </button>
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-sm font-medium cursor-not-allowed border border-gray-200 dark:border-gray-700"
                  title="This template requires review approval before posting"
                >
                  <Send className="h-4 w-4" /> Approve and Post to NS
                </button>
                <p className="text-center text-xs text-amber-600 dark:text-amber-400">
                  Mandate Review is on — send for review first. After approval, the Post button will unlock.
                </p>
              </div>
            ) : (
              /* ── No mandate review, OR review already done: Post is primary action ── */
              <div className="flex flex-col w-full max-w-md gap-2">
                <button
                  onClick={handlePost}
                  disabled={!canPost}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-navy-700 hover:bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow"
                >
                  {posting
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Posting…</>
                    : <><Send className="h-4 w-4" /> Approve and Post to NS</>}
                </button>
              </div>
            )}

            {/* Detail already lives in the checklist card above — just point back
                up to it so the button's disabled state isn't unexplained. */}
            {unmatchedSummaryItems.length > 0 && !posting && !postResult?.success && (
              <button
                type="button"
                onClick={() => setShowChecklist(true)}
                className="text-center text-xs text-amber-600 dark:text-amber-400 hover:underline"
              >
                {unmatchedSummaryItems.length} issue{unmatchedSummaryItems.length !== 1 ? 's' : ''} to resolve before posting — see checklist above
              </button>
            )}
          </div>
        </div>
        </div>
        )}
      </section>

      {/* Draggable divider — drag to resize Fields vs document panels */}
      <div
        onMouseDown={isPdfViewerVisible ? handleDividerMouseDown : undefined}
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        className={`relative flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-teal-400 dark:hover:bg-teal-500 active:bg-teal-500 group z-10 overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isPdfViewerVisible ? 'w-1 opacity-100 cursor-col-resize' : 'w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
      </div>

      {/* RIGHT: Document viewer */}
      <div
        style={{ width: isPdfViewerVisible ? `${100 - leftPanelPct}%` : '0%' }}
        className={`flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isPdfViewerVisible ? 'min-w-[300px] opacity-100' : 'min-w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="flex-1 overflow-hidden">
          {pdfDocId ? <PDFViewer documentId={pdfDocId} highlightedField={highlightedField} /> : (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
              <Upload className="h-8 w-8 opacity-30" />
              <p className="text-sm">No PDF available</p>
            </div>
          )}
        </div>
      </div>

      </div>{/* end two-panel body */}
    </div>{/* end page */}

    {/* ── NS Posting Progress Modal ── */}
    {posting && (
      postMinimized ? (
        <button onClick={() => setPostMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Posting to NS…</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{postProgress.percent}% — click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 ml-1" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setPostMinimized(true)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-10 animate-pulse" />
            <div className="relative flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg mb-4">
                <Bot className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Posting to NetSuite</h3>
              <div className="w-full mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{postProgress.message || 'Starting…'}</span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{postProgress.percent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(postProgress.percent, 2)}%` }} />
                </div>
              </div>
              <div className="w-full space-y-2 text-left">
                {[
                  { label: 'Prepare fields', threshold: 5 },
                  { label: 'Validate against NS', threshold: 25 },
                  { label: 'Create PO in NetSuite', threshold: 55 },
                  { label: 'Add line items', threshold: 80 },
                  { label: 'Finalise & save', threshold: 100 },
                ].map(step => {
                  const done = postProgress.percent >= step.threshold
                  const active = postProgress.percent >= step.threshold - 25 && postProgress.percent < step.threshold
                  return (
                    <div key={step.label} className="flex items-center gap-2">
                      {done ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : active ? <Loader2 className="h-3.5 w-3.5 text-indigo-500 animate-spin flex-shrink-0" /> : <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />}
                      <span className={`text-xs ${done ? 'text-green-600 dark:text-green-400 font-medium' : active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>{step.label}</span>
                    </div>
                  )
                })}
              </div>
              <p className="mt-4 text-[10px] text-gray-400">Click outside to minimise</p>
            </div>
          </div>
        </div>
      )
    )}

    {/* ── Cancel Request Modal ── */}
    {showCancelModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel Request</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
            </div>
          </div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for cancellation <span className="text-red-500">*</span></label>
          <textarea
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            rows={3}
            placeholder="Enter reason…"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => { setShowCancelModal(false); setCancelReason('') }}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Keep Request
            </button>
            <button
              onClick={handleCancel}
              disabled={!cancelReason.trim() || cancelling}
              className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              {cancelling ? 'Cancelling…' : 'Cancel Request'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
